import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import {
  auth,
  db,
  authReady as authReadyPromise,
} from './lib/firebase';
import {
  onAuthStateChanged,
  signOut,
  signInWithCustomToken,
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs, onSnapshot, limit as fsLimit } from 'firebase/firestore';
const ApplicantFlow = React.lazy(() => import('./components/ApplicantFlow'));
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const EmployerDashboard = React.lazy(() => import('./components/EmployerDashboard'));
const ReferrerDashboard = React.lazy(() => import('./components/ReferrerDashboard'));
const SponsorDashboard = React.lazy(() => import('./components/sponsor/SponsorDashboard'));
const EmployerOnboarding = React.lazy(() => import('./components/EmployerOnboarding'));
const ReferrerOnboarding = React.lazy(() => import('./components/ReferrerOnboarding'));
const SponsorOnboarding = React.lazy(() => import('./components/SponsorOnboarding').then(m => ({ default: m.SponsorOnboarding })));
const AuthPage = React.lazy(() => import('./components/AuthPage'));
const GetStartedPage = React.lazy(() => import('./components/platform/GetStartedPage'));
const GetStartedSignedInNotice = React.lazy(() => import('./components/platform/GetStartedPage').then(m => ({ default: m.GetStartedSignedInNotice })));
const ProfileCompletionForm = React.lazy(() => import('./components/ProfileCompletionForm'));
const PendingApproval = React.lazy(() => import('./components/PendingApproval'));
const AcceptOrgInvitation = React.lazy(() => import('./components/AcceptOrgInvitation'));
const PublicSigningView = React.lazy(() => import('./components/public/PublicSigningView'));
import VerificationBanner from './components/VerificationBanner';
import WorkspaceTeamInvitePrompt from './components/WorkspaceTeamInvitePrompt';
import OrgRestrictionGate from './components/OrgRestrictionGate';
import PasswordSetForm from './components/shared/PasswordSetForm';
import ErrorBoundary from './components/ErrorBoundary';
import SimulationBanner from './components/SimulationBanner';
import { INACTIVE_ACCOUNT_BANNER } from './lib/orgApproval';
import { getAdminSidebarTabs, JOBS_AREA_TAB_IDS } from './lib/adminNavStructure';
import { ADMIN_ONLY_PATH_SEGMENTS } from './lib/adminTabs';
import { UserProfile } from './types';
import { safeFetch } from './lib/api';
import { normalizeEmail } from './lib/identity';
import { joinPersonName, sanitizeNamePart } from './lib/nameUtils';
import { onForegroundMessage } from './lib/messaging';
import { brand, applyBrandToDOM } from './lib/branding';
import { getPreAuthBrand, toBrandConfig } from './lib/preAuthBranding';
import { ThemeProvider } from './lib/design/theme';
import PwaUpdater from './components/PwaUpdater';
import PwaInstallBanner from './components/PwaInstallBanner';
import { GlobalToastLayer, GlobalConfirmLayer } from './components/shared/ToastLayer';
import { notifyToast, clearAllToasts } from './lib/toastStore';
import { WorkspaceProvider, useWorkspaces } from './components/shared/WorkspaceProvider';
import { getActiveWorkspaceId, workspaceHeader } from './lib/workspaces';
import WorkspaceAccessDenied from './components/shared/WorkspaceAccessDenied';
import BottomNav, { type TabConfig } from './components/BottomNav';
import { isOrgPendingReview, orgRoleBranchesReady, orgOnboardingGate, isOrgTerminalInactiveStatus, isReferrerSourcedSuspension, isReferrerRestricted, roleScreenReady, createOrgStatusTrustState, resolveOrgStatusRound, applyOrgStatusRound, ORG_NEGATIVE_RECHECK_MS, type OrgStatusTrustState } from './lib/accountStatus';
import { useAppVersion } from './hooks/useAppVersion';
import { usePath, navigate, parseRoute } from './lib/router';
import { hasModuleAccess, isWorkspaceTeamRole } from './lib/permissions';
import RoleSelectionScreen, { getRoleOptionsForUser, computeRoleOptionFlags, getNonEnhancedReferrerOptions, RoleOption } from './components/shared/RoleSelectionScreen';
import TeamSelectionGate from './components/shared/TeamSelectionGate';
import { hasApplicantRole as hasApplicantRoleFromProfile } from './lib/becomeReferrer';
import { switchRoleWithReload, requestRoleValidation, roleAuthHeaders, rememberedRoleKey, REMEMBERED_ROLE_KEY, AUTH_UID_SESSION_KEY, type RoleSwitchControl } from './lib/roleSwitch';
import UserMenu from './components/shared/UserMenu';
import ShellSkeleton from './components/shared/ShellSkeleton';

// TEAMS & ROLES (condensed mirror — canonical note: see above
// isOrgConsoleProfile in serverWorkspaces.ts): WORKSPACE_TEAM_ROLES below
// are the WORKSPACE TEAM (internal staff) only. Organization teams
// (Referrer / Employer / Sponsor Team) live on org docs' linkedUsers[]
// and route to their own dashboards — never through this set. The
// workspaceMembers 'admin' stamp on approved org owners denotes ORG
// OWNERSHIP, not staff membership; resolveWorkspaceTeamRole below must
// not be "simplified" into trusting that stamp for org profiles.
// Workspace team roles that route to the workspace dashboard (scoped by
// hasModuleAccess + server-side applicant scope) rather than the applicant flow.
const WORKSPACE_TEAM_ROLES = new Set(['admin', 'manager', 'member', 'team_member', 'viewer', 'master', 'owner', 'super']);
const NON_TEAM_WORKSPACE_ROLES = new Set(['applicant', 'employer', 'sponsor', 'referrer']);

/**
 * Unwrap a stored `workspaceMembers[workspaceId]` value to its role name.
 * Memberships are plain role strings from current flows but legacy data may
 * store an object ({ role: 'admin', addedAt }). A raw String(...) on the
 * object yields '[object object]', which silently fails WORKSPACE_TEAM_ROLES
 * matching and routes workspace admins/members to the applicant flow instead
 * of the Admin Dashboard.
 */
function workspaceMemberRoleOf(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const r = (value as any)?.role ?? (value as any)?.name;
    if (typeof r === 'string') return r;
  }
  return '';
}

// TEAMS & ROLES: this derivation interprets the workspaceMembers stamp for
// WORKSPACE TEAM routing only — see the canonical note above isOrgConsoleProfile in serverWorkspaces.ts.
/**
 * Detect whether a profile belongs to a workspace team member (as opposed to
 * an end-user applicant or an employer/referrer/sponsor account) and return
 * their effective workspace role (lowercase), or '' when they are not a team
 * member. Sources, in order of precedence:
 *   1. profile.workspaceRole set to a built-in team role
 *   2. profile.role set to a built-in team role (create-and-add writes this)
 *   3. non-empty workspaceMembers containing at least one non-organization
 *      membership (covers existing accounts added to a workspace as members)
 */
function resolveWorkspaceTeamRole(profile: UserProfile): string {
  if (!profile) return '';
  const wsRole = workspaceMemberRoleOf(profile.workspaceRole).toLowerCase();
  if (WORKSPACE_TEAM_ROLES.has(wsRole)) return wsRole;
  const pRole = String(profile.role || '').toLowerCase();
  if (WORKSPACE_TEAM_ROLES.has(pRole)) return pRole;
  const memberships = profile.workspaceMembers ? Object.entries(profile.workspaceMembers) : [];
  if (memberships.length > 0) {
    const hasNonOrgMembership = memberships.some(([, r]) => !NON_TEAM_WORKSPACE_ROLES.has(workspaceMemberRoleOf(r).toLowerCase()));
    if (hasNonOrgMembership) {
      const activeId = getActiveWorkspaceId() || memberships[0][0];
      const activeRole = workspaceMemberRoleOf(memberships.find(([id]) => id === activeId)?.[1] || memberships[0][1]);
      return String(activeRole || 'member').toLowerCase();
    }
  }
  return '';
}

function getPathFromTab(tabId: string): string {
  if (tabId === 'dashboard') return '/dashboard';
  if (tabId === 'workflow') return '/admin/workflow';
  if (tabId === 'applicants' || tabId === 'application') return '/applications';
  if (tabId === 'my-events') return '/myevents';
  if (tabId === 'events') return '/events';
  return `/${tabId}`;
}

// ADMIN_ONLY_PATH_SEGMENTS is imported from lib/adminTabs (ADM-22):
// it is DERIVED from the canonical ADMIN_TAB_IDS list there — the
// hand-maintained copy that used to live here had silently dropped
// 'documents', 'automations', 'reports' and 'senders'. ('senders' has
// since been retired as a tab by ADM-08, so it derives no segment.)

function getTabFromPathSegment(segment: string, role: { isAdmin: boolean; isEmployer: boolean; isReferrer: boolean; isSponsor: boolean }): string {
  const norm = segment.toLowerCase();
  if (!role.isAdmin && ADMIN_ONLY_PATH_SEGMENTS.includes(norm)) return 'dashboard';
  if (norm === 'dashboard') return 'dashboard';
  if (norm === 'workflow') return 'workflow';
  if (norm === 'applications' || norm === 'applicants') {
    if (role.isAdmin || role.isEmployer) return 'applicants';
    return 'application';
  }
  if (norm === 'myevents' || norm === 'my-events') {
    return 'my-events';
  }
  if (norm === 'events') {
    return 'events';
  }
  // Check special cases
  if (norm === 'candidates') return 'candidates';
  if (norm === 'team' || norm === 'agents') {
    if (role.isEmployer || role.isSponsor || role.isReferrer) return 'agents';
    return 'profile';
  }
  return norm;
}
import CollapsibleSidebar from './components/CollapsibleSidebar';
import PublicScheduleView from './components/PublicScheduleView';
import PublicScreeningBookingView from './components/PublicScreeningBookingView';
import PublicAgentBookingView from './components/PublicAgentBookingView';
import PublicGroupScheduleView from './components/PublicGroupScheduleView';
import PublicSpecialRequestView from './components/PublicSpecialRequestView';
import PublicPaymentView from './components/PublicPaymentView';
import { LegalDocumentPage, PrivacyRequestPage, UnsubscribePage } from './components/legal/LegalPages';
import LegalFooter from './components/legal/LegalFooter';
import PublicQuoteView from './components/PublicQuoteView';
import {
  Home,
  FileText,
  Calendar,
  Briefcase,
  LifeBuoy,
  UserCircle,
  LayoutDashboard,
  Layers,
  Mail,
  Users,
  User,
  UserCheck,
  Building,
  CalendarDays,
  TicketCheck,
  MessageCircle,
  DollarSign,
  Link,
  BookOpen,
  BarChart3,
  Award,
  Settings,
  HelpCircle,
  Download,
  Shield,
  ShieldCheck,
  Clock,
  ClipboardList,
  Building2,
  BadgeCheck,
  Radar,
  MessageSquare,
  Star,
  Bookmark,
  PieChart,
  ShieldAlert,
  Sparkles,
  Lock,
  GitFork,
} from 'lucide-react';

/** Renders the Access Denied screen when the active workspace request is denied. */
function WorkspaceGate({ onLogout, children }: { onLogout: () => void; children: React.ReactNode }) {
  const { accessDenied } = useWorkspaces();
  if (accessDenied) return <WorkspaceAccessDenied onLogout={onLogout} />;
  return <>{children}</>;
}

export default function App() {
  // ── Apply branding on mount ──
  // The effective pre-auth brand: build-time VITE_ brand, with the host
  // workspace's auth-config branding merged over it when that config has
  // already resolved (syncWorkspaceTenantAuth runs at module boot). When
  // it hasn't, this is exactly the build-time brand — and when the config
  // resolves later, applyAuthConfigBranding re-applies the merged brand.
  React.useEffect(() => { applyBrandToDOM(toBrandConfig(getPreAuthBrand())); }, []);

  // ── FIX 2026-10-07: Clear stale invite params ──
  // If a signed-in user lands on a non-invitation route with stale invite
  // params (?token=...&kind=team), the workspace routing crashes. Consume and
  // clear them immediately.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const hasInviteParams = url.searchParams.has('token') &&
      (url.searchParams.get('kind') === 'team' || url.pathname !== '/accept-invitation');
    if (hasInviteParams && url.pathname !== '/accept-invitation') {
      // Stale invite params on a non-invitation route — strip them
      url.searchParams.delete('token');
      url.searchParams.delete('kind');
      url.searchParams.delete('email');
      url.searchParams.delete('ws');
      url.searchParams.delete('id');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  }, []);

  // ── Follow-up Item 10(b): capture the referrer invite code ──
  // Referrer dashboards hand out /invite?ref=REFxxxxxx links, but nothing
  // ever read the parameter — self-signups via the link were never
  // stamped with the referrer. Persist the code for the session so the
  // application-create call can forward it and the server can stamp the
  // linkage (referredBy / referrerId / referrerName) authoritatively.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const ref = new URL(window.location.href).searchParams.get('ref');
      if (ref && /^REF[0-9A-Za-z]{6}$/.test(ref.trim())) {
        sessionStorage.setItem('isats_ref_code', ref.trim().toUpperCase());
        // PR-14: mirror to localStorage — sessionStorage is per-tab, so
        // an applicant who opened the invite link, then completed signup
        // in a fresh tab (or returned later) lost the code before any
        // server call could persist it on their user record. The mirror
        // survives tab changes on the same browser; the server-side
        // persist (upsert claim / create / draft) remains the durable
        // cross-session source once any of those calls lands.
        try { localStorage.setItem('isats_ref_code', ref.trim().toUpperCase()); } catch { /* mirror is best-effort */ }
      }
    } catch { /* storage unavailable — the signup simply proceeds unstamped */ }
  }, []);

  // ── Simulation mode detection ──
  // When ?simToken=xxx is present (opened from "Simulate User" in admin panel),
  // the app switches to session-only persistence and signs in as the target user.
  const simTokenRef = useRef<string | null>(null);
  const simUidRef = useRef<string | null>(null);
  const simEmailRef = useRef<string | null>(null);
  const [simulationMode, setSimulationMode] = useState<{ uid: string; email: string } | null>(null);
  const simulationHandledRef = useRef(false);

  // Read simulation params from URL or sessionStorage on first render
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (!simTokenRef.current) {
      simTokenRef.current = params.get('simToken');
      simUidRef.current = params.get('simUid');
      simEmailRef.current = params.get('simEmail');
      const adminUidParam = params.get('adminUid');
      const adminEmailParam = params.get('adminEmail');
      if (adminUidParam) sessionStorage.setItem('isats_admin_uid', adminUidParam);
      if (adminEmailParam) sessionStorage.setItem('isats_admin_email', adminEmailParam);
    }
  }

  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [authReady, setAuthReady] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean>(false);
  const [isEmployer, setIsEmployer] = useState<boolean>(false);
  const [isReferrer, setIsReferrer] = useState<boolean>(false);
  const [isSuperAgent, setIsSuperAgent] = useState<boolean>(true);
  // NOTE: `referrerEnhanced` (server truth for the enhanced/non-enhanced
  // referrer tier from /api/referrer/onboard-status) is declared below with
  // the role-status state as `boolean | null` (null = not yet resolved).
  // All consumers treat non-`true` as not-enhanced (fail-closed), so the
  // enhanced-only nav is never shown until the server confirms the tier.
  const [isSponsor, setIsSponsor] = useState<boolean>(false);

  // Multi-role selection state
  const [selectedSessionRole, setSelectedSessionRole] = useState<string | null>(() => {
    return sessionStorage.getItem('isats_selected_role');
  });
  const [multiRoleOptions, setMultiRoleOptions] = useState<RoleOption[]>([]);
  // True once the multi-role detection has computed the option list for this
  // session. The role-selection screen must NEVER render from a guessed or
  // partial list — while unresolved, callers render the loader instead.
  // (Fixes the intermittent 3-card render: a stale list flashing before
  // server-truth options resolved.)
  const [roleOptionsResolved, setRoleOptionsResolved] = useState(false);
  // "Become a referrer": an existing applicant going through the shared
  // referrer onboarding wizard. Rendered instead of the applicant
  // app-shell while the wizard is open (the wizard is the new-referrer
  // creation state, even though the account itself is returning).
  const [becomeReferrerWizard, setBecomeReferrerWizard] = useState(false);
  // Forced inactive banner: set when the session poll detects the account was
  // deactivated (server revoked the tokens). Renders ONLY the "Account
  // Inactive" banner — never the login screen, dashboard, onboarding, or role
  // screen — even though the Firebase session is signed out.
  const [forceDeactivatedBanner, setForceDeactivatedBanner] = useState(false);
  const [employerInfo, setEmployerInfo] = useState<{ id: string; companyName: string } | null>(null);
  const [employerOnboarded, setEmployerOnboarded] = useState<boolean | null>(null);
  const [referrerOnboarded, setReferrerOnboarded] = useState<boolean | null>(null);
  /**
   * Post-auth route gate (pending-approval persistence fix).
   *
   * Role state starts as `null` and is reset to `null` on logout, so the
   * org-role branches must not render until checkRoleStatus() has resolved
   * for the current session. Without this gate, a re-login renders the
   * onboarding wizard from stale state while the status fetch is in flight —
   * stranding a submitted user mid-onboarding until a manual reload.
   */
  const [roleStatusResolved, setRoleStatusResolved] = useState(false);
  // null = approval status not yet resolved for this session. The pending
  // gate must only render on explicit `false` — never on unknown — so an
  // approved account can never flash the "under review" screen while the
  // status check is in flight (finding #2).
  const [employerApproved, setEmployerApproved] = useState<boolean | null>(null);
  const [referrerApproved, setReferrerApproved] = useState<boolean | null>(null);
  // Raw employer status (pending / rejected / deactivated …), mirroring
  // referrerStatus — the terminal-inactive branch (D-18) keys off it.
  const [employerStatus, setEmployerStatus] = useState<string | null>(null);
  // A pending referrer record created before the full onboarding form was
  // completed (e.g. self-service signup placeholder) is "incomplete" until the
  // referrer finishes their profile — route them through ReferrerOnboarding.
  const [referrerProfileIncomplete, setReferrerProfileIncomplete] = useState(false);
  // Raw referrer status (pending / rejected / invited …) so onboarding can be
  // re-shown for profiles that need to be re-submitted after a rejection.
  const [referrerStatus, setReferrerStatus] = useState<string | null>(null);
  // PR-13b: the referrer org doc id from the status round — compared
  // against the user doc's suspendedOrgId to prove a deactivation stamp
  // was sourced by the referrer org (restricted mode) rather than by an
  // employer/sponsor org (hard lockout).
  const [referrerOrgId, setReferrerOrgId] = useState<string | null>(null);
  // Whether the referrer holds Enhanced Referrer Access (server truth from
  // /api/referrer/onboard-status). null = not yet resolved. Non-enhanced
  // referrers get a MANDATORY role-selection step on every login (Applicant
  // Entry + Referrer); enhanced referrers keep the #1074 single-role skip.
  const [referrerEnhanced, setReferrerEnhanced] = useState<boolean | null>(null);
  // True when a role-status check (onboard-status) failed. The UI must keep
  // the loader with a retry affordance — never fall through to onboarding,
  // dashboard, or role screens on unknown status.
  const [roleStatusFailed, setRoleStatusFailed] = useState(false);
  // PR-10: once ANY status round has completed successfully this session,
  // the resolved state is STICKY — a later transient round failure (slow
  // network, cold start, an aborted re-round) must not replace a resolved
  // dashboard with the error screen or re-open the wizard gate. Failure
  // state is therefore only surfaced while nothing has ever resolved;
  // after that, a failed round simply keeps the last known server truth.
  const roleStatusEverResolvedRef = useRef(false);
  // PR-13: per-role onboard-status trust (see accountStatus.ts). A single
  // NEGATIVE answer is never routed on: it must be confirmed by consecutive
  // agreeing rounds, and any positive answer is sticky for the session —
  // so one wrong "no record" answer (server lookup glitch, stale cache)
  // can no longer flash the onboarding wizard at an approved user.
  const referrerTrustRef = useRef<OrgStatusTrustState>(createOrgStatusTrustState());
  const employerTrustRef = useRef<OrgStatusTrustState>(createOrgStatusTrustState());
  // Pending fast re-check while a negative answer is unconfirmed, and a
  // ref to the latest checkRoleStatus so the timer can fire it without
  // a stale closure.
  const negativeRecheckTimerRef = useRef<number | null>(null);
  const checkRoleStatusRef = useRef<() => void>(() => {});
  const scheduleNegativeRecheck = useCallback(() => {
    if (negativeRecheckTimerRef.current !== null) return;
    negativeRecheckTimerRef.current = window.setTimeout(() => {
      negativeRecheckTimerRef.current = null;
      checkRoleStatusRef.current();
    }, ORG_NEGATIVE_RECHECK_MS);
  }, []);
  // §1.5: role switch in flight. Set SYNCHRONOUSLY in the click handler —
  // before the async server validation — so the click acknowledges in the
  // same frame (progress overlay), instead of a multi-second dead gap while
  // validation + hard reload run. Cleared when validation fails (the reload
  // on success makes clearing it moot).
  const [roleSwitchTarget, setRoleSwitchTarget] = useState<string | null>(null);
  // §1.5: boot deadline. If the signed-in boot sequence (auth init / profile
  // load) has not completed in BOOT_STUCK_MS, the gateway screen swaps its
  // endless spinner for the retry affordance instead of hanging forever.
  const [bootStuck, setBootStuck] = useState(false);
  const [sponsorApproved, setSponsorApproved] = useState<boolean | null>(null);
  const [sponsorStatus, setSponsorStatus] = useState<string>('pending');
  // D-23: the admin's stored rejection reason for the signed-in user's OWN
  // org record, carried by the per-role status endpoints (they only include
  // it while the record is rejected). Feeds the PendingApproval notice so a
  // rejected user sees the actual reason instead of generic fallback text.
  const [employerRejectionReason, setEmployerRejectionReason] = useState('');
  const [referrerRejectionReason, setReferrerRejectionReason] = useState('');
  const [sponsorRejectionReason, setSponsorRejectionReason] = useState('');
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);
  // Chat Phase 0: the nav unread badge that used to be fed here read the
  // `conversations` PARENT docs (rules deny those reads: `allow read,
  // write: if false`) and counted `unreadForApplicant`/`unreadCount`
  // fields nothing ever writes — the listener could only ever error
  // (swallowed) and the badge was permanently dead. Removed rather than
  // left as a fake indicator; real unread state is a Phase 1 concern
  // (server-side read state + conversation summaries).

  const path = usePath();
  const route = parseRoute(path);

  const getInitialTab = (): string => {
    if (typeof window === 'undefined') return 'dashboard';
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/admin/workflow')) return 'workflow';
    const r = parseRoute(currentPath);
    if (r.type === 'workflow_state' || r.section === 'workflow') return 'workflow';
    if (r.type === 'applications_detail') return 'applicants';
    if (r.type === 'events_detail') return 'my-events';
    if (r.type === 'sponsors_detail') return 'sponsors';
    if (r.type === 'employers_detail') return 'employers';
    if (r.type === 'referrers_detail') return 'referrers';
    if (r.type === 'programs_detail') return 'settings';
    if (r.type === 'organizations_detail') return 'organizations';
    if (r.type === 'users_detail') return 'users';
    if (r.type === 'tickets_detail') return 'tickets';
    if (r.type === 'base') {
      const norm = r.section.toLowerCase();
      if (norm === 'applications' || norm === 'applicants') return 'applicants';
      if (norm === 'events' || norm === 'my-events') return 'my-events';
      return norm;
    }
    return 'dashboard';
  };

  const [activeNavTab, setActiveNavTab] = useState<string>(getInitialTab);

  const getRoleFlags = () => ({
    isAdmin,
    isEmployer,
    isReferrer,
    isSponsor,
  });

  // Sync activeNavTab when path or roles change
  useEffect(() => {
    const roles = getRoleFlags();
    let targetTab = 'dashboard';
    if (route.type === 'applications_detail') {
      targetTab = roles.isAdmin || roles.isEmployer ? 'applicants' : 'application';
    } else if (route.type === 'events_detail') {
      targetTab = path.startsWith('/myevents') || path.startsWith('/my-events') ? 'my-events' : 'events';
    } else if (route.type === 'sponsors_detail') {
      targetTab = 'sponsors';
    } else if (route.type === 'employers_detail') {
      targetTab = 'employers';
    } else if (route.type === 'referrers_detail') {
      targetTab = 'referrers';
    } else if (route.type === 'programs_detail') {
      targetTab = 'settings';
    } else if (route.type === 'organizations_detail') {
      targetTab = 'organizations';
    } else if (route.type === 'users_detail') {
      targetTab = 'users';
    } else if (route.type === 'tickets_detail') {
      targetTab = 'tickets';
    } else if (route.type === 'base') {
      targetTab = getTabFromPathSegment(route.section, roles);
    }

    if (activeNavTab !== targetTab) {
      setActiveNavTab(targetTab);
      window.dispatchEvent(new CustomEvent('nav:change', { detail: { tab: targetTab } }));
    }
  }, [path, isAdmin, isEmployer, isReferrer, isSponsor]);

  // A workspace invite link must reach the AuthPage flow. If the visitor is
  // already signed in (e.g. to a different workspace), sign them out first so
  // the invite is honoured instead of silently swallowed.
  useEffect(() => {
    if (typeof window === 'undefined' || !user) return;
    if (new URLSearchParams(window.location.search).has('inviteToken')) {
      handleLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Sync browser path when activeNavTab changes
  useEffect(() => {
    if (!authReady || !user) return;
    const currentPath = window.location.pathname;
    // This list MUST mirror the public routes the render block below
    // recognises (isPublicRoute). It previously omitted /sign/:token
    // (and the other newer public routes), so a SIGNED-IN visitor who
    // opened a signing link was silently navigated away to their tab
    // path the moment auth settled — the dead signing link never got
    // to show its error screen (live finding L2). Keep the two lists
    // in step.
    const isPublic = !!(
      currentPath.match(/^\/group-schedule\/[a-zA-Z0-9_-]+$/) ||
      currentPath.match(/^\/schedule\/[a-zA-Z0-9_-]+$/) ||
      currentPath.match(/^\/book-screening$/) ||
      currentPath.match(/^\/(?:book-agent|book)\/[a-zA-Z0-9_-]+$/) ||
      currentPath.match(/^\/special-request\/[a-zA-Z0-9_-]+$/) ||
      currentPath.match(/^\/pay\/[a-zA-Z0-9_-]+$/) ||
      currentPath.match(/^\/quote\/[a-zA-Z0-9_-]+$/) ||
      currentPath.match(/^\/sign\/[a-zA-Z0-9_-]+$/) ||
      currentPath.match(/^\/(privacy|terms|refund-policy|cookies|accessibility)$/) ||
      currentPath.match(/^\/privacy-request$/) ||
      currentPath.match(/^\/unsubscribe$/)
    );
    if (isPublic) return;

    const targetPath = getPathFromTab(activeNavTab);

    // Keep detailed sub-route if already on it and tab is still matching
    const routeInfo = parseRoute(currentPath);
    if (routeInfo.type === 'applications_detail' && (activeNavTab === 'applicants' || activeNavTab === 'application')) {
      return;
    }
    if (routeInfo.type === 'events_detail' && (activeNavTab === 'events' || activeNavTab === 'my-events')) {
      return;
    }
    if (routeInfo.type === 'sponsors_detail' && activeNavTab === 'sponsors') {
      return;
    }
    if (routeInfo.type === 'employers_detail' && activeNavTab === 'employers') {
      return;
    }
    if (routeInfo.type === 'referrers_detail' && activeNavTab === 'referrers') {
      return;
    }
    if (routeInfo.type === 'programs_detail' && activeNavTab === 'settings') {
      return;
    }
    if (routeInfo.type === 'organizations_detail' && activeNavTab === 'organizations') {
      return;
    }
    if (routeInfo.type === 'users_detail' && activeNavTab === 'users') {
      return;
    }
    if (routeInfo.type === 'tickets_detail' && activeNavTab === 'tickets') {
      return;
    }

    if (currentPath !== targetPath) {
      navigate(targetPath);
    }
  }, [activeNavTab, authReady, user]);

  const [profileLoadTimeout, setProfileLoadTimeout] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const docJustCreated = useRef(false);

  // Self-Serve Onboarding D-0 + D-8 (owner decisions, 2026-10-14): the
  // public workspace signup route (/get-started) is for signed-out
  // visitors ONLY, and the portal NEVER redirects a user into a
  // workspace — the URL determines the context, workspaces own their
  // users, and the platform stays invisible. So there is deliberately
  // NO navigation here: a PRE-EXISTING signed-in session that reaches
  // /get-started gets a neutral platform-context notice (see the route
  // guard below), and the form never renders for an authenticated
  // session. The single exception is the signup flow's OWN handoff:
  // GetStartedPage flags (via onHandoffSessionStart) that the session
  // about to exist was provisioned in THIS tab, so the guard keeps the
  // page mounted and its done step performs the landing itself. The
  // flag is per-tab, in-memory only, and clears as soon as the route
  // is left — a fresh arrival with a session is never auto-landed.
  const [getStartedHandoff, setGetStartedHandoff] = useState(false);
  useEffect(() => {
    if (path !== '/get-started' && getStartedHandoff) setGetStartedHandoff(false);
  }, [path, getStartedHandoff]);
  // ── Deployment update detection ──
  const { updateAvailable: versionPollUpdate } = useAppVersion();
  const [swTriggeredUpdate, setSwTriggeredUpdate] = useState(false);
  const [updateBannerDismissed, setUpdateBannerDismissed] = useState(false);

  useEffect(() => {
    const handler = () => setSwTriggeredUpdate(true);
    window.addEventListener('isats:update-available', handler);
    return () => window.removeEventListener('isats:update-available', handler);
  }, []);

  // After a SW-triggered reload, preserve the existing Firebase Auth session.
  // browserLocalPersistence stores the auth token in localStorage, which survives
  // page reloads and Service Worker updates — no sign-out needed.
  // Clean up 'update_reload_at' after its 15s suppression window expires so
  // future polls (useAppVersion, PwaUpdater) can detect real updates.
  useEffect(() => {
    const reloadedAt = sessionStorage.getItem('update_reload_at');
    if (!reloadedAt || Date.now() - parseInt(reloadedAt, 10) >= 15_000) {
      sessionStorage.removeItem('update_reload_at');
    }
  }, []);

  const showUpdateBanner = versionPollUpdate || swTriggeredUpdate;

  // Navigate via custom event — lets child components (ApplicantFlow, AdminDashboard, etc.)
  // listen for tab changes without coupling their internal view state to this parent.
  const handleNavChange = useCallback((tabId: string) => {
    setActiveNavTab(tabId);
    window.dispatchEvent(new CustomEvent('nav:change', { detail: { tab: tabId } }));
  }, []);

  // Dashboard widgets dispatch nav:change directly (see DashboardShell onNavigate)
  // — keep the dark sidebar's active highlight in sync with those too.
  useEffect(() => {
    const handler = (e: Event) => {
      const tab = (e as CustomEvent).detail?.tab;
      if (tab) setActiveNavTab(tab);
    };
    window.addEventListener('nav:change', handler);
    return () => window.removeEventListener('nav:change', handler);
  }, []);

  // Toggle body class for bottom-nav padding on mobile
  const showNav = !!user && !!profile && authReady;
  useEffect(() => {
    if (showNav) {
      document.body.classList.add('has-bottom-nav');
    } else {
      document.body.classList.remove('has-bottom-nav');
    }
    return () => document.body.classList.remove('has-bottom-nav');
  }, [showNav]);

  // If profileCompleted is still undefined, wait up to 3s for Firestore to settle,
  // but skip the wait entirely if we just created the document in this session.
  useEffect(() => {
    if (profile && profile.profileCompleted === undefined && !isAdmin && !isEmployer && !isReferrer && !isSponsor) {
      if (docJustCreated.current) {
        setProfileLoadTimeout(true);
        return;
      }
      const t = setTimeout(() => setProfileLoadTimeout(true), 3000);
      return () => clearTimeout(t);
    }
  }, [profile, isAdmin, isEmployer, isReferrer, isSponsor]);

  // Warm up Cloud Run on app load — delayed 3s so auth/Firestore requests finish first
  useEffect(() => {
    const t = setTimeout(() => fetch('/api/health').catch(() => {}), 3000);
    return () => clearTimeout(t);
  }, []);

  // ── Simulation mode: intercept auth flow ──────────────────────────────
  // Runs before the normal auth flow. When simToken is present, we switch
  // to session-only persistence (isolated per-tab) and sign in as the target
  // user. This does NOT affect the admin's original tab when opened via
  // window.open('_blank') because a new tab has a completely isolated JS
  // heap, Firebase Auth instance, and sessionStorage.
  //
  // CRITICAL: Simulation MUST be opened in a new tab (window.open), NOT an
  // iframe.  An iframe with allow-same-origin shares the parent window's
  // Firebase Auth singleton — signInWithCustomToken in the iframe would
  // trigger onAuthStateChanged in the parent and kick out the admin.
  useEffect(() => {
    const token = simTokenRef.current;
    const targetUid = simUidRef.current || '';
    const targetEmail = simEmailRef.current || '';
    if (!token || simulationHandledRef.current) return;

    // Clean URL params FIRST so a page refresh never retries the simulation
    const url = new URL(window.location.href);
    url.searchParams.delete('simToken');
    url.searchParams.delete('simUid');
    url.searchParams.delete('simEmail');
    window.history.replaceState({}, '', url.toString());

    // Mark handled immediately (synchronous) so this effect never re-runs
    simulationHandledRef.current = true;

    setLoading(true);

    authReadyPromise.then(async () => {
      try {
        // Save the admin's identity from the custom token's simBy claim if not already set.
        const decoded = JSON.parse(atob(token.split('.')[1]));
        if (decoded.simBy && !sessionStorage.getItem('isats_admin_uid')) {
          sessionStorage.setItem('isats_admin_uid', decoded.simBy);
        }
        // Save target simulation metadata in sessionStorage for persistence across page reloads
        sessionStorage.setItem('isats_sim_uid', targetUid);
        sessionStorage.setItem('isats_sim_email', targetEmail);

        // Per-tab session storage was already selected at Auth
        // initialization (src/lib/firebase.ts): this tab's Auth uses
        // browserSessionPersistence from the start, so the admin's
        // locally persisted session is never read or migrated here.
        // (L3: a setPersistence switch at this point used to delete
        // the admin's stored session from the shared store.)
        await signInWithCustomToken(auth, token);
        // Force ID token refresh so the readOnly claim propagates to Firestore rules
        await auth.currentUser?.getIdToken(true);
        // Clean up URL query parameters so a manual browser refresh uses the active session
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('simToken');
        cleanUrl.searchParams.delete('simUid');
        cleanUrl.searchParams.delete('simEmail');
        cleanUrl.searchParams.delete('adminUid');
        cleanUrl.searchParams.delete('adminEmail');
        window.history.replaceState({}, '', cleanUrl.toString());
        // Set simulation mode (Banner won't render until user is non-null)
        setSimulationMode({ uid: targetUid, email: targetEmail });
      } catch (err) {
        console.error('Simulation sign-in failed:', err);
        setSimulationMode(null);
        setLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    // Wait for setPersistence to resolve before subscribing to auth state,
    // preventing a flash of the login screen for returning users.
    authReadyPromise.then(() => {
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      setAuthReady(false);
      setIsAdmin(false);
      setProfileLoadTimeout(false);
      setProfileLoadError(false);
      // A fresh sign-in clears any forced deactivated banner (e.g. after
      // reactivation). A sign-out (null) leaves it — the banner stays until
      // the user explicitly signs out from it or signs in again.
      if (firebaseUser) setForceDeactivatedBanner(false);

      if (firebaseUser && sessionStorage.getItem('pending_login_otp_email')) {
        // AuthPage may temporarily sign in with Firebase client auth only to
        // prove the password when server-side REST verification lacks runtime
        // config. Do not hydrate the app or read the user profile until the
        // required login OTP has completed.
        // This can occur during the client-side password verification fallback.
        // Tear down all hydrated state synchronously so a previous dashboard
        // cannot remain mounted while the required OTP is outstanding.
        setUser(null);
        setProfile(null);
        setIsAdmin(false);
        setIsEmployer(false);
        setIsReferrer(false);
        setIsSponsor(false);
        setAuthReady(false);
        setLoading(false);
        return;
      }

      // Check if we are in simulation mode
      const adminUid = sessionStorage.getItem('isats_admin_uid');
      if (adminUid && firebaseUser) {
        setSimulationMode({ uid: firebaseUser.uid, email: firebaseUser.email || '' });
      } else {
        setSimulationMode(null);
      }

      // Track the resolved profile in a local variable so the catch block
      // can check it without reading stale React state (Bug 2 fix).
      let resolvedProfile: UserProfile | null = null;

      if (firebaseUser) {
        // Hoist these to function scope so the catch block (retry path) can read them
        let isAdminClaim = false;
        let detectedEmployer = false;

        try {
          // Use cached token — force=true would add 500ms–2s round-trip on every load
          const tokenResult = await firebaseUser.getIdTokenResult(false);
          isAdminClaim = tokenResult.claims.admin === true;
          setIsAdmin(isAdminClaim);

          // P0-1a: no client-side PII keys exist anymore — payloads
          // are decrypted server-side, so there is no key fetch to
          // await before mounting protected components.
          setUser(firebaseUser);

          // Read user doc first — always succeeds for the user's own doc.
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

          // Platform Super Administrator — a global role OUTSIDE all workspaces.
          // Detected from the profile so platform admins route to the admin
          // dashboard even before the custom claim propagates to a fresh token.
          if (userDoc.exists() && (userDoc.data()?.role === 'platform_admin' || userDoc.data()?.primaryRole === 'platform_admin')) {
            isAdminClaim = true;
            setIsAdmin(true);
            setIsPlatformAdmin(true);
          }

          // FIX (BUG-1): Tier 2 employer query (by userId) is only safe for users
          // who already have the employer custom claim. For plain applicants the
          // Firestore rule is `allow list: if isAdmin()` — the query throws
          // permission-denied even though it's inside a try/catch, because the
          // SDK's offline-persistence layer enqueues the 403 and it leaks into
          // subsequent write commits, corrupting the queue for application creation.
          // Gate it behind the employer claim so applicants never touch /employers.
          let empByUserIdSnap = null;
          if (!isAdminClaim && tokenResult.claims.employer === true && firebaseUser.email) {
            try {
              empByUserIdSnap = await getDocs(
                query(collection(db, 'employers'), where('userId', '==', firebaseUser.uid), fsLimit(1))
              );
            } catch {
              // Silently ignore — Tier 4 (role field) handles the fallback.
            }
          }

          let profileData: UserProfile;
          if (userDoc.exists()) {
            profileData = userDoc.data() as UserProfile;
          } else {
            profileData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              role: isAdminClaim ? 'admin' : 'applicant',
              status: 'incomplete',
              createdAt: new Date().toISOString()
            };
            await setDoc(doc(db, 'users', firebaseUser.uid), profileData);
            docJustCreated.current = true;
          }

          // VULN-1: Reject unverified non-admin emails. Elevated routing must
          // never be granted from an unverified email string; server-side APIs
          // also re-check current organization linkage/status on each action.
          if (!isAdminClaim && !firebaseUser.emailVerified) {
            await signOut(auth);
            setUser(null);
            setProfile(null);
            setAuthReady(false);
            setLoading(false);
            return;
          }

          // ── Primary Role Pre-check ────────────────────────────────
          const pRole = profileData.primaryRole || profileData.role || null;
          if (pRole === 'admin' || pRole === 'platform_admin') {
            isAdminClaim = true;
          }
          const isProfilePlatformAdmin = pRole === 'platform_admin' || tokenResult.claims.platformAdmin === true;
          if (isProfilePlatformAdmin) {
            setIsPlatformAdmin(true);
            (profileData as any).platformAdmin = true;
            (profileData as any).isPlatformAdmin = true;
          }

          // ── Employer Detection ───────────────────────────────────
          // Three-tier strategy:
          //   1. Firebase custom claims (fast path)
          //   2. employers collection by userId field (claim-gated, safe)
          //   3. employers collection by contactEmail — only for users with
          //      employer claim (avoids list permission-denied for applicants)
          //   4. Fallback: trust role field on user doc
          // ──────────────────────────────────────────────────────────
          detectedEmployer = pRole === 'employer';
          let empInfo: { id: string; companyName: string } | null = null;
          const email = firebaseUser.email || '';
          const normalEmail = normalizeEmail(email);

          // Tier 1: Custom claims (multi-valued employerIds; legacy employerId fallback).
          if (!isAdminClaim && tokenResult.claims.employer === true) {
            detectedEmployer = true;
            if (empByUserIdSnap && !empByUserIdSnap.empty) {
              const empData = empByUserIdSnap.docs[0].data() as any;
              empInfo = { id: empByUserIdSnap.docs[0].id, companyName: empData.companyName || '' };
            } else {
              const ids = Array.isArray(tokenResult.claims.employerIds)
                ? tokenResult.claims.employerIds.filter((x: any) => typeof x === 'string')
                : [];
              const legacy = typeof tokenResult.claims.employerId === 'string' ? tokenResult.claims.employerId : '';
              empInfo = { id: ids[0] || legacy || '', companyName: '' };
            }
          }

          // Tier 2: employers collection by userId (reuses claim-gated query above)
          if (!detectedEmployer && !isAdminClaim && email && empByUserIdSnap) {
            if (!empByUserIdSnap.empty) {
              const empData = empByUserIdSnap.docs[0].data() as any;
              detectedEmployer = true;
              empInfo = { id: empByUserIdSnap.docs[0].id, companyName: empData.companyName || '' };
            }
          }

          // Tier 3: employers collection by contactEmail or email.
          // FIX (BUG-1): ONLY run when employer claim is present — list queries on
          // /employers throw permission-denied for non-admin applicants, leaking
          // 403s into the Firestore SDK commit queue and breaking subsequent writes.
          if (!detectedEmployer && !isAdminClaim && tokenResult.claims.employer === true && firebaseUser.emailVerified && email) {
            try {
              const empSnap = await getDocs(
                query(collection(db, 'employers'), where('contactEmail', '==', normalEmail), fsLimit(1))
              );
              if (!empSnap.empty) {
                const empData = empSnap.docs[0].data() as any;
                detectedEmployer = true;
                empInfo = { id: empSnap.docs[0].id, companyName: empData.companyName || '' };
              } else {
                const emailSnap = await getDocs(
                  query(collection(db, 'employers'), where('email', '==', normalEmail), fsLimit(1))
                );
                if (!emailSnap.empty) {
                  const empData = emailSnap.docs[0].data() as any;
                  detectedEmployer = true;
                  empInfo = { id: emailSnap.docs[0].id, companyName: empData.companyName || '' };
                }
              }

              // If detected via email, sync custom claims in background for fast logins next time
              if (detectedEmployer) {
                firebaseUser.getIdToken().then(token => fetch('/api/admin/employers/sync-claims', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({}),
                })).catch(() => {});
              }
            } catch { /* non-critical: employer detection fallback */ }
          }

          // Tier 4 fallback: trust the role field on the user document.
          // Also handles employer users whose claim is set but userId/email
          // field lookup failed above.
          if (!detectedEmployer && !isAdminClaim && profileData.role === 'employer') {
            detectedEmployer = true;
            // empInfo stays null — EmployerDashboard must handle null employerInfo gracefully
          }

          // ── Referrer Detection ─────────────────────────────────────
          let detectedReferrer = Boolean(
            tokenResult.claims.referrer === true ||
            pRole === 'referrer' ||
            profileData.role === 'referrer' ||
            profileData.referrerId ||
            Object.values(profileData.workspaceMembers || {}).includes('referrer')
          );
          if (!detectedReferrer && firebaseUser.uid) {
            try {
              const refSnap = await getDocs(
                query(collection(db, 'referrers'), where('userId', '==', firebaseUser.uid), fsLimit(1))
              );
              if (!refSnap.empty) detectedReferrer = true;
            } catch { /* non-critical */ }
          }
          setIsReferrer(detectedReferrer);

          // ── Sponsor Detection ─────────────────────────────────────
          let detectedSponsor = Boolean(
            tokenResult.claims.sponsor === true ||
            pRole === 'sponsor' ||
            profileData.role === 'sponsor' ||
            profileData.sponsorId ||
            Object.values(profileData.workspaceMembers || {}).includes('sponsor')
          );

          if (detectedSponsor) {
            const isApproved = profileData.sponsorStatus === 'active' || profileData.sponsorStatus === 'approved' || tokenResult.claims.sponsor === true;
            setSponsorApproved(isApproved);
            setSponsorStatus(profileData.sponsorStatus || (isApproved ? 'active' : 'pending'));
            if (normalEmail) {
              firebaseUser.getIdToken().then(token => fetch('/api/admin/sponsors/sync-claims', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({}),
              })).catch(() => {});
            }
          }
          setIsSponsor(detectedSponsor);

          // ── Multi-Role Evaluation ───────────────────────────────────
          // NOTE: computeRoleOptionFlags keeps org-console accounts
          // (referrer/employer/sponsor) off the "Workspace Team Member"
          // option — their workspaceMembers[ws]='admin' stamp means "org
          // owner" (written by mutateUserRole on org approval), never team
          // membership. See RoleSelectionScreen.computeRoleOptionFlags.
          const detectedTeamRole = resolveWorkspaceTeamRole(profileData);
          const hasEmployerRole = detectedEmployer;
          const hasReferrerRole = detectedReferrer;
          const hasSponsorRole = detectedSponsor;
          const hasApplicantRole = hasApplicantRoleFromProfile(profileData);
          const roleFlags = computeRoleOptionFlags({
            isAdminClaim,
            detectedTeamRole,
            detectedEmployer: hasEmployerRole,
            detectedReferrer: hasReferrerRole,
            detectedSponsor: hasSponsorRole,
            hasApplicant: hasApplicantRole,
          });
          const hasTeamRole = roleFlags.hasTeam;

          const roleOptions = getRoleOptionsForUser({
            hasTeam: roleFlags.hasTeam,
            hasEmployer: roleFlags.hasEmployer,
            hasReferrer: roleFlags.hasReferrer,
            hasSponsor: roleFlags.hasSponsor,
            hasApplicant: roleFlags.hasApplicant,
            teamRoleLabel: profileData.workspaceRole || detectedTeamRole,
          });

          setMultiRoleOptions(roleOptions);
          // Detection complete for this session — the role screen may now
          // render from this server-truth list. Never render it earlier.
          setRoleOptionsResolved(true);

          // Resolve active active session role
          let activeRoleToUse = selectedSessionRole;
          if (roleOptions.length === 1) {
            activeRoleToUse = roleOptions[0].id;
          } else if (roleOptions.length > 1 && (!activeRoleToUse || !roleOptions.some(r => r.id === activeRoleToUse))) {
            activeRoleToUse = null; // Multi-role user needs to make an explicit selection
          }

          // Apply state based on activeRoleToUse
          if (activeRoleToUse === 'team') {
            isAdminClaim = true;
            setIsAdmin(true);
            setIsEmployer(false);
            setIsReferrer(false);
            setIsSponsor(false);
            if (!profileData.workspaceRole) {
              profileData.workspaceRole = detectedTeamRole === 'super' ? 'admin' : (detectedTeamRole || 'admin');
            }
          } else if (activeRoleToUse === 'employer') {
            isAdminClaim = false;
            setIsAdmin(false);
            setIsEmployer(true);
            setIsReferrer(false);
            setIsSponsor(false);
          } else if (activeRoleToUse === 'referrer') {
            isAdminClaim = false;
            setIsAdmin(false);
            setIsEmployer(false);
            setIsReferrer(true);
            setIsSponsor(false);
          } else if (activeRoleToUse === 'sponsor') {
            isAdminClaim = false;
            setIsAdmin(false);
            setIsEmployer(false);
            setIsReferrer(false);
            setIsSponsor(true);
          } else if (activeRoleToUse === 'applicant') {
            isAdminClaim = false;
            setIsAdmin(false);
            setIsEmployer(false);
            setIsReferrer(false);
            setIsSponsor(false);
          } else {
            // Unselected multi-role state
            setIsAdmin(false);
            setIsEmployer(false);
            setIsReferrer(false);
            setIsSponsor(false);
          }

          setEmployerInfo(empInfo);

          // Preserve the canonical platform role for labels and platform-only
          // visibility. `isAdmin` still selects the workspace dashboard.
          // Note: For workspace team members (non-admins), preserve their team role
          // on `profileData.workspaceRole` / `profileData.role` so permission
          // checks do not mistake them for workspace admins.
          const currentWsRole = profileData.workspaceRole || resolveWorkspaceTeamRole(profileData);
          if (currentWsRole) {
            profileData.workspaceRole = currentWsRole === 'super' ? 'admin' : currentWsRole;
          }
          const isTrueWorkspaceAdmin = isProfilePlatformAdmin || ['admin', 'owner', 'super', 'master'].includes(String(profileData.workspaceRole || '').toLowerCase());
          profileData.role = isProfilePlatformAdmin ? 'platform_admin' : (isAdminClaim && isTrueWorkspaceAdmin) ? 'admin' : detectedEmployer ? 'employer' : detectedReferrer ? 'referrer' : detectedSponsor ? 'sponsor' : (profileData.workspaceRole || profileData.role || null);

          // Pre-fetch effective identity permissions if workspace team member to eliminate UI flickering
          if (isAdminClaim && profileData.workspaceRole) {
            try {
              const idRes = await safeFetch('/api/profile/effective-identity');
              if (idRes?.identity?.permissions) {
                profileData.permissions = idRes.identity.permissions;
                if (idRes.identity.applicantScope) {
                  profileData.applicantScope = idRes.identity.applicantScope;
                }
              }
            } catch { /* proceed with failsafe permissions */ }
          }

          resolvedProfile = profileData;
          setProfile(profileData);

          // FIX (BUG-2): upsertUserIdOnApplications previously ran 3× getDocs list
          // queries directly on /applications from the client. Firestore rules restrict
          // list to `resource.data.userId == request.auth.uid` which cannot be evaluated
          // on a collection-wide scan — on cold start / slow token propagation these
          // throw permission-denied, leaking 403s into the SDK queue.
          // Moved to a server-side API call so writes happen via Admin SDK (no rules).
          // The server endpoint is idempotent and sets userIdMigrated=true on success.
          // Follow-up Item 10: the userIdMigrated flag made this a
          // ONE-SHOT claim — a referrer-added candidate created AFTER the
          // user's first migration was never stamped with their userId,
          // so it stayed invisible to userId-keyed surfaces (workflow
          // board). The endpoint is idempotent; run it once per browser
          // session instead of once per account lifetime.
          let claimAlreadyDoneThisSession = false;
          try { claimAlreadyDoneThisSession = sessionStorage.getItem(`isats_uid_upsert:${firebaseUser.uid}`) === '1'; } catch { /* ignore */ }
          if (!claimAlreadyDoneThisSession && firebaseUser.uid === profileData.uid) {
            firebaseUser.getIdToken().then(token => {
              fetch('/api/applications/upsert-user-id', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  uid: firebaseUser.uid,
                  email: normalizeEmail(firebaseUser.email || profileData.email),
                  // PR-7: hand the server the invite code captured at
                  // landing so it can be persisted on the user record —
                  // the stamp fallback when the application is created
                  // in a later session (sessionStorage does not survive).
                  referralCode: (() => { try { return sessionStorage.getItem('isats_ref_code') || localStorage.getItem('isats_ref_code') || undefined; } catch { return undefined; } })(),
                }),
              }).then(() => {
                try { sessionStorage.setItem(`isats_uid_upsert:${firebaseUser.uid}`, '1'); } catch { /* ignore */ }
              }).catch(() => {}); // fire-and-forget, non-critical
            }).catch(() => {});
          }
        } catch (e: any) {
          // ── CHANGE 1: Widen the retry condition — catch ALL Firestore errors,
          //    not just offline. deadline-exceeded, failed-precondition, and
          //    internal errors all occur on cold starts. ──
          const isRetryable =
            e?.code === 'unavailable' ||
            e?.code === 'deadline-exceeded' ||
            e?.code === 'failed-precondition' ||
            e?.code === 'internal' ||
            e?.code === 'permission-denied' || // ← auth-propagation race on cold login
            e?.message?.includes('offline') ||
            e?.message?.includes('deadline');

          if (isRetryable) {
            const delays = [800, 1800, 3000];
            for (const delay of delays) {
              try {
                await new Promise(r => setTimeout(r, delay));
                // Force token refresh in case auth state propagation was delayed
                await firebaseUser.getIdToken(true).catch(() => {});
                const retryDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
                if (retryDoc.exists()) {
                  const profileData = retryDoc.data() as UserProfile;
                  const isRetryEmployer = !isAdminClaim && (profileData.role === 'employer' || detectedEmployer);
                  const isRetryReferrer = !isAdminClaim && !isRetryEmployer && profileData.role === 'referrer';
                  const isRetrySponsor = !isAdminClaim && !isRetryEmployer && !isRetryReferrer && profileData.role === 'sponsor';
                  const isRetryTeamMember = !isAdminClaim && !isRetryEmployer && !isRetryReferrer && !isRetrySponsor && !!resolveWorkspaceTeamRole(profileData);
                  if (isRetryTeamMember) {
                    isAdminClaim = true;
                    setIsAdmin(true);
                    if (!profileData.workspaceRole) profileData.workspaceRole = resolveWorkspaceTeamRole(profileData);
                  }
                  const retryWsRole = profileData.workspaceRole || resolveWorkspaceTeamRole(profileData);
                  if (retryWsRole) {
                    profileData.workspaceRole = retryWsRole === 'super' ? 'admin' : retryWsRole;
                  }
                  const isRetryTrueAdmin = ['admin', 'owner', 'super', 'master'].includes(String(profileData.workspaceRole || '').toLowerCase());
                  profileData.role = (isAdminClaim && isRetryTrueAdmin) ? 'admin' : isRetryEmployer ? 'employer' : isRetryReferrer ? 'referrer' : isRetrySponsor ? 'sponsor' : (profileData.workspaceRole || 'applicant');
                  if (isRetryEmployer) {
                    setIsEmployer(true);
                  }
                  if (isRetryReferrer) {
                    setIsReferrer(true);
                  }
                  if (isRetrySponsor) {
                    setIsSponsor(true);
                    setSponsorApproved(profileData.sponsorStatus === 'active' || profileData.sponsorStatus === 'approved' || tokenResult.claims.sponsor === true);
                  }
                  resolvedProfile = profileData;
                  setProfile(profileData);
                  break;
                }
              } catch { /* non-critical: retry attempt failed, try next delay */ }
            }
          }
          if (!resolvedProfile) {
            console.error('PROFILE LOAD FAILED — no stub created:', e?.code, e?.message);
            // ── CHANGE 3: Do NOT call setProfile(stub) — a stub with no profile
            //    fields would incorrectly route the user to ProfileCompletionForm.
            //    Instead, return early with profile=null so the loading screen
            //    persists. The user can refresh and the retry will succeed once
            //    Firestore has warmed up. ──
            setIsAdmin(false);
            setAuthReady(false);
            setAuthInitialized(true);
            setLoading(false);
            // Show a recovery UI instead of an infinite silent spinner
            setProfileLoadError(true);
            return; // ← exit without setting a stub profile
          }
        }

        setAuthReady(true);
      } else {
        setSimulationMode(null);
        setUser(null);
        setProfile(null);
        setIsAdmin(false);
        setIsEmployer(false);
        setIsReferrer(false);
        setIsSponsor(false);
        setSponsorApproved(false);
        setAuthReady(false);
        // MED-1: Clear all chat caches on sign-out to prevent stale data
        // from leaking between user sessions on shared devices
        try {
          Object.keys(sessionStorage)
            .filter(k => k.startsWith('chat_cache_'))
            .forEach(k => sessionStorage.removeItem(k));
        } catch {}
      }
      setAuthInitialized(true);
      setLoading(false);
    }); // onAuthStateChanged
    }); // authReadyPromise.then

    return () => { if (unsubscribe) unsubscribe(); };
  }, []);

  // Re-fetch profile when profileRefreshKey changes (e.g. after profile completion)
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const profileData = userDoc.data() as UserProfile;
        // Re-detect employer, referrer, and sponsor from the user document instead of relying on stale state
        const isDocEmployer = profileData.role === 'employer';
        const isDocReferrer = profileData.role === 'referrer';
        const isDocSponsor = profileData.role === 'sponsor';
        const isDocPlatformAdmin = profileData.role === 'platform_admin' || profileData.primaryRole === 'platform_admin';
        // Workspace team member (admin/manager/member/viewer/custom) — route to
        // the workspace dashboard with restricted module scope.
        const isDocTeamMember = !isAdmin && !isDocEmployer && !isDocReferrer && !isDocSponsor && !!resolveWorkspaceTeamRole(profileData);
        setIsPlatformAdmin(isDocPlatformAdmin);
        if (isDocTeamMember) {
          setIsAdmin(true);
          if (!profileData.workspaceRole) profileData.workspaceRole = resolveWorkspaceTeamRole(profileData);
        }
        if (isDocPlatformAdmin) {
          (profileData as any).platformAdmin = true;
          (profileData as any).isPlatformAdmin = true;
        }
        const docWsRole = profileData.workspaceRole || resolveWorkspaceTeamRole(profileData);
        if (docWsRole) {
          profileData.workspaceRole = docWsRole === 'super' ? 'admin' : docWsRole;
        }
        const isDocTrueAdmin = isDocPlatformAdmin || ['admin', 'owner', 'super', 'master'].includes(String(profileData.workspaceRole || '').toLowerCase());
        profileData.role = isDocPlatformAdmin ? 'platform_admin' : (isAdmin && isDocTrueAdmin) ? 'admin' : isDocEmployer ? 'employer' : isDocReferrer ? 'referrer' : isDocSponsor ? 'sponsor' : (profileData.workspaceRole || profileData.role || null);
        if (isDocPlatformAdmin) setIsAdmin(true);
        if (isDocEmployer) setIsEmployer(true);
        if (isDocReferrer) setIsReferrer(true);
        if (isDocSponsor) { setIsSponsor(true); setSponsorApproved(profileData.sponsorStatus === 'active' || profileData.sponsorStatus === 'approved'); }
        // The stored SESSION role is the view truth. Doc detection above
        // only ever SETS role flags — without this re-assert, a mid-session
        // profile refresh flips a dual-role user back to their
        // doc-canonical role (e.g. viewing My Application, saving the
        // profile modal, and landing on the referrer dashboard). That is
        // the same stale-flip class that made in-place role switches
        // no-op (G22); re-apply the boot mapping for the session pick.
        try {
          const sessionRole = sessionStorage.getItem('isats_selected_role');
          if (sessionRole === 'applicant' || sessionRole === 'referrer' || sessionRole === 'employer' || sessionRole === 'sponsor') {
            if (!isDocPlatformAdmin) setIsAdmin(false);
            setIsEmployer(sessionRole === 'employer');
            setIsReferrer(sessionRole === 'referrer');
            setIsSponsor(sessionRole === 'sponsor');
          }
        } catch { /* storage unavailable */ }
        setProfile(profileData);
        setAuthReady(true);
      }
    } catch (e) {
      console.error('Profile refresh failed:', e);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    if (profileRefreshKey > 0) {
      refreshProfile();
    }
  }, [profileRefreshKey, refreshProfile]);

  // ── Employer / Referrer onboarding status check ─────────────────────
  // Runs once when auth/role changes and is re-invoked on a timer while a
  // profile is pending approval so the user is moved straight into their
  // dashboard the moment an admin approves — no manual refresh needed.
  const checkRoleStatus = useCallback((signal?: AbortSignal | null) => {
    if (!user || !authReady) return;
    const s = signal || null;
    // Fresh round: clear any prior failure/enhanced state so a retry or
    // re-login re-resolves from server truth.
    setRoleStatusFailed(false);
    setReferrerEnhanced(null);
    const token = user.getIdToken ? user.getIdToken() : Promise.resolve('');
    token.then(async (t: string) => {
      // Per-round failure tracking for sticky resolution (PR-10): the
      // failure is only surfaced while no round has ever resolved — after
      // that, a failed round keeps the last known server truth on screen
      // instead of flip-flopping the user between dashboard, the error
      // screen, and (via the local user-doc hints) the onboarding wizard.
      let roundFailed = false;
      const markRoundFailed = () => {
        roundFailed = true;
        if (!roleStatusEverResolvedRef.current) setRoleStatusFailed(true);
      };
      try {
      if (s?.aborted) return;
      // PR-13b: send the workspace header like every other workspace-
      // scoped call (AGENTS.md: raw fetches to workspace-scoped
      // endpoints MUST spread workspaceHeader()). Without it the server
      // resolved these status reads under the DEFAULT workspace while
      // the org record is stamped with another workspace — one half of
      // the PR-13 regression (the server now also resolves self-status
      // across contexts, so the truth no longer depends on this header).
      const headers = t ? { Authorization: `Bearer ${t}`, ...workspaceHeader() } : {};
      if (isEmployer) {
        try {
          const r = await fetch('/api/employer/onboard-status', { headers, signal: s ?? undefined });
          // On HTTP failure the status is UNKNOWN — never fabricate
          // `{ onboarded: false }`; track the failure so the UI keeps the
          // loader (with retry) instead of dropping into onboarding.
          if (!r.ok) {
            if (s?.aborted) return;
            markRoundFailed();
            return;
          }
          const d = await r.json();
          if (s?.aborted) return;
          // PR-13: same trust rule as the referrer branch — a single
          // NEGATIVE answer is unconfirmed. Keep the last known routing
          // state (fresh session: unresolved → loader) and re-check
          // promptly; only consecutive agreeing negatives are applied.
          const trust = resolveOrgStatusRound(employerTrustRef.current, d.onboarded ? 'positive' : 'negative', Date.now());
          employerTrustRef.current = trust.state;
          if (!trust.apply) {
            if (trust.needsRecheck) scheduleNegativeRecheck();
          } else {
            setEmployerOnboarded(!!d.onboarded);
            setEmployerApproved(d.status === 'active' || d.status === 'approved');
            setEmployerStatus(d.status || 'pending');
            setEmployerRejectionReason(typeof d.rejectionReason === 'string' ? d.rejectionReason : '');
            // EMP-07: the Employer Team nav entry is gated on this, the
            // same way the referrer branch below sets it for its own
            // tier-filtered tab list.
            if (d.isSuperAgent !== undefined) {
              setIsSuperAgent(d.isSuperAgent);
            }
          }
        } catch {
          // A transient network failure must NOT bounce an onboarded user back
          // into onboarding — only an explicit "not onboarded" answer may.
          if (s?.aborted) return;
          markRoundFailed();
        }
      }
      if (isReferrer) {
        try {
          const r = await fetch('/api/referrer/onboard-status', { headers, signal: s ?? undefined });
          // On HTTP failure the status is UNKNOWN — never fabricate
          // `{ onboarded: false }` here; that drops approved users into the
          // re-onboarding wizard on a transient network blip. The failure is
          // tracked so the UI keeps the loader (with retry) instead.
          if (!r.ok) {
            if (s?.aborted) return;
            markRoundFailed();
            return;
          }
          const d = await r.json();
          if (s?.aborted) return;
          // PR-13: a single NEGATIVE answer is UNCONFIRMED — it is not
          // applied to the routing state at all. The last known state
          // stands (fresh session: unresolved → the branches stay on the
          // loader) and a fast re-check is scheduled; only consecutive
          // agreeing negatives spanning a few seconds confirm it, and
          // once any round has answered positive, negatives are vetoed
          // for the rest of the session. (The server half of PR-13 makes
          // lookup failures return 500 instead of a fabricated negative;
          // this trust rule covers any wrong negative regardless of
          // source — e.g. a stale per-instance cache entry.)
          const trust = resolveOrgStatusRound(referrerTrustRef.current, d.onboarded ? 'positive' : 'negative', Date.now());
          referrerTrustRef.current = trust.state;
          if (!trust.apply) {
            if (trust.needsRecheck) scheduleNegativeRecheck();
          } else {
          setReferrerOnboarded(!!d.onboarded);
          const approved = d.status === 'active' || d.status === 'approved';
          setReferrerApproved(approved);
          setReferrerStatus(d.status || 'pending');
          setReferrerRejectionReason(typeof d.rejectionReason === 'string' ? d.rejectionReason : '');
          if (d.referrerId) setReferrerOrgId(d.referrerId);
          // Server truth for Enhanced Referrer Access (default false).
          setReferrerEnhanced(d.enhancedPermission === true);
          if (d.isSuperAgent !== undefined) {
            setIsSuperAgent(d.isSuperAgent);
          }
          // Tier truth for the enhanced/non-enhanced referrer split — drives
          // the sidebar/mobile nav filtering below. Never derive this from
          // isSuperAgent: every org owner is a super agent, enhanced or not.
          if (d.enhancedPermission !== undefined) {
            setReferrerEnhanced(d.enhancedPermission === true);
          }
          // While awaiting review, check whether the referrer profile still
          // needs completing (bare self-service placeholder) so the user is
          // routed through ReferrerOnboarding to finish it before approval.
          if (d.onboarded && !approved) {
            try {
              const pr = await fetch('/api/referrer/profile', { headers, signal: s ?? undefined });
              if (s?.aborted) return;
              // PR-10: only a SUCCESSFUL profile response may update the
              // completeness flag. Previously a non-OK response (transient
              // 500, cold start, a stale-cache 404 on another instance)
              // produced pd = null → complete = false → the submitted
              // user was manufactured "profile incomplete", which vetoed
              // the pending-review gate and bounced them back into the
              // onboarding wizard until a later round happened to
              // succeed. On failure, leave the last known completeness —
              // the same rule the catch below already stated.
              if (pr.ok) {
                const pd = await pr.json().catch(() => null);
                const p = pd?.referrer;
                const complete = !!(p && p.name && p.contactPhone && Array.isArray(p.relatedPrograms) && p.relatedPrograms.length > 0);
                setReferrerProfileIncomplete(!complete);
              }
            } catch {
              /* leave the last known completeness — don't bounce the user */
            }
          }
          } // PR-13: end of trust.apply else (unconfirmed negatives skip application)
        } catch {
          if (s?.aborted) return;
          // Network failure: status is UNKNOWN. Never fabricate `false` —
          // that drops approved users into the re-onboarding wizard. Track
          // the failure so the UI keeps the loader (with retry) instead.
          markRoundFailed();
        }
      }
      if (isSponsor) {
        // Sponsor approval is determined during role detection (sponsorId
        // presence in the user doc). The async check here only syncs the
        // user's Firestore doc if the API finds an active sponsor doc,
        // but NEVER downgrades to pending — the detection is authoritative.
        try {
          const r = await fetch('/api/sponsor/status', { headers, signal: s ?? undefined });
          const d = r.ok ? await r.json() : { active: false };
          if (s?.aborted) return;
          if (d?.active === true) {
            setSponsorApproved(true); // Only upgrade — never downgrade
            setSponsorRejectionReason('');
          } else if (typeof d?.rejectionReason === 'string' && d.rejectionReason) {
            // D-23: the owner's own rejection reason for the notice.
            setSponsorRejectionReason(d.rejectionReason);
          }
        } catch { /* ignore entirely */ }
      }
      } finally {
        // Mark the post-auth route gate resolved once this round completes.
        // Aborted rounds don't count — the re-run effect starts a fresh
        // round that will resolve instead.
        if (!s?.aborted) {
          setRoleStatusResolved(true);
          // Sticky resolution (PR-10): a round that completed without a
          // failure pins the resolved state — later failed rounds keep
          // the last known server truth instead of tearing it down.
          if (!roundFailed) roleStatusEverResolvedRef.current = true;
        }
      }
    }).catch(() => {
      if (!roleStatusEverResolvedRef.current) setRoleStatusFailed(true);
      setRoleStatusResolved(true);
    });
  }, [user, authReady, isEmployer, isReferrer, isSponsor, scheduleNegativeRecheck]);

  // PR-13: keep the recheck ref pointed at the latest checkRoleStatus so
  // the unconfirmed-negative timer fires a current-closure round.
  useEffect(() => {
    checkRoleStatusRef.current = () => { checkRoleStatus(); };
  }, [checkRoleStatus]);

  useEffect(() => {
    if (!user || !authReady) return;
    const abortController = new AbortController();
    checkRoleStatus(abortController.signal);
    // Safety net: never trap the user on the route-gate loader if the
    // status check hangs (no response, no abort). Normal resolution is
    // well under a second; this only fires on a stuck network round.
    // §1.5: a hung round means status is UNKNOWN — surface the failure
    // (loader + Retry) rather than resolving into branches rendered from
    // unresolved state, which could misroute (e.g. flash the wizard).
    // PR-10: once a round has resolved successfully, a hung background
    // re-round must not tear the resolved state down — the failure is
    // only surfaced while nothing has ever resolved.
    const fallback = window.setTimeout(() => {
      if (!roleStatusEverResolvedRef.current) setRoleStatusFailed(true);
      setRoleStatusResolved(true);
    }, 15000);
    return () => { abortController.abort(); window.clearTimeout(fallback); };
  }, [user, authReady, isEmployer, isReferrer, isSponsor, checkRoleStatus]);

  // Mandatory role selection for non-enhanced referrers. A referrer WITHOUT
  // Enhanced Referrer Access must pick a context on every login — exactly
  // "My Application" and "Referrer Dashboard" (never "Workspace Team Member"). The
  // options are forced from server truth (referrerEnhanced from
  // /api/referrer/onboard-status); any in-flight auto-selection is cleared so
  // the choice is explicit. Enhanced referrers keep the #1074 single-role
  // skip; all other roles are unaffected.
  const forcedSelectionAppliedRef = useRef(false);
  useEffect(() => {
    if (!isReferrer || isAdmin || referrerEnhanced !== false || forcedSelectionAppliedRef.current) return;
    forcedSelectionAppliedRef.current = true;
    const forcedOptions = getNonEnhancedReferrerOptions();
    setMultiRoleOptions(forcedOptions);
    // Mandatory selection is enforced at LOGIN, not on every reload. On a
    // same-tab reload the stored value is the user's own explicit choice —
    // restore it instead of forcing re-selection. Only clear when there is
    // no valid stored choice (fresh login).
    const stored = sessionStorage.getItem('isats_selected_role');
    if (stored && forcedOptions.some(r => r.id === stored)) {
      setSelectedSessionRole(stored);
    } else {
      sessionStorage.removeItem('isats_selected_role');
      setSelectedSessionRole(null);
    }
  }, [isReferrer, isAdmin, referrerEnhanced]);

  // FIX 2026-10-07: Auto-apply remembered role choice.
  // If the user has a remembered role in localStorage and it's in the available
  // options, apply it automatically — EXCEPT for non-enhanced referrers on fresh
  // login (mandatory explicit choice per product requirement).
  const rememberedRoleAppliedRef = useRef(false);
  useEffect(() => {
    if (rememberedRoleAppliedRef.current) return;
    if (!roleOptionsResolved || multiRoleOptions.length <= 1) return;
    if (selectedSessionRole) return; // Already have a session choice
    // Skip for non-enhanced referrers (mandatory explicit choice)
    if (isReferrer && !isAdmin && referrerEnhanced === false) return;
    // §1.2: the remembered role is scoped to THIS account — on a shared
    // browser another account's pick must never auto-apply here.
    let remembered: string | null = null;
    try { remembered = localStorage.getItem(rememberedRoleKey(user?.uid)); } catch {}
    if (remembered && multiRoleOptions.some(r => r.id === remembered)) {
      rememberedRoleAppliedRef.current = true;
      sessionStorage.setItem('isats_selected_role', remembered);
      setSelectedSessionRole(remembered);
    }
  }, [roleOptionsResolved, multiRoleOptions, selectedSessionRole, isReferrer, isAdmin, referrerEnhanced, user?.uid]);

  // §1.2: publish the signed-in uid where the role-switch persistence layer
  // reads it (so new remembered-role writes are uid-scoped), and drop the
  // legacy UNSCOPED remembered-role key left by older builds — it cannot be
  // attributed to an account, so it must never steer a landing again.
  useEffect(() => {
    if (!user?.uid) return;
    try { sessionStorage.setItem(AUTH_UID_SESSION_KEY, user.uid); } catch { /* storage unavailable */ }
    try { localStorage.removeItem(REMEMBERED_ROLE_KEY); } catch { /* storage unavailable */ }
  }, [user?.uid]);

  // Auto-advance polling: while an org role is not yet approved, keep
  // checking so an approval elsewhere moves the user into their dashboard
  // automatically — whether they are sitting on the PendingApproval gate OR
  // still inside the onboarding wizard (§1.3: the pending screen's promise
  // "this screen automatically becomes your dashboard" must be true).
  useEffect(() => {
    if (!user || !authReady) return;
    const awaitingApproval =
      (isEmployer && employerApproved === false) ||
      (isReferrer && referrerApproved === false) ||
      (isSponsor && sponsorApproved === false);
    if (!awaitingApproval) return;
    const id = window.setInterval(() => { checkRoleStatus(); }, 15000);
    return () => window.clearInterval(id);
  }, [user, authReady, isEmployer, employerApproved, isReferrer, referrerApproved, isSponsor, sponsorApproved, checkRoleStatus]);

  // §1.3: refetch org status the moment the user returns to the tab/window
  // while an org role is unapproved — approval most often lands while the
  // user is off checking email, so focus is the highest-signal moment.
  useEffect(() => {
    if (!user || !authReady) return;
    const awaitingApproval =
      (isEmployer && employerApproved !== true) ||
      (isReferrer && referrerApproved !== true) ||
      (isSponsor && sponsorApproved !== true);
    if (!awaitingApproval) return;
    const refetch = () => { checkRoleStatus(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') refetch(); };
    window.addEventListener('focus', refetch);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refetch);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, authReady, isEmployer, employerApproved, isReferrer, referrerApproved, isSponsor, sponsorApproved, checkRoleStatus]);

  // §1.2: once an org role is APPROVED, its onboarding drafts are dead
  // state — clear the local draft and the server draft so a later
  // re-review / resubmission can never resurrect a stale pre-approval
  // draft over the approved record. Fires once per approval transition.
  const approvedDraftClearedRef = useRef<string>('');
  useEffect(() => {
    if (!user?.uid) return;
    const approvedRoles: Array<'employer' | 'referrer' | 'sponsor'> = [];
    if (isEmployer && employerApproved === true) approvedRoles.push('employer');
    if (isReferrer && referrerApproved === true) approvedRoles.push('referrer');
    if (isSponsor && sponsorApproved === true) approvedRoles.push('sponsor');
    for (const role of approvedRoles) {
      const mark = `${user.uid}:${role}`;
      if (approvedDraftClearedRef.current === mark) continue;
      approvedDraftClearedRef.current = mark;
      try { localStorage.removeItem(`onboarding:draft:${role}:${user.uid}`); } catch { /* storage unavailable */ }
      // Best-effort server-draft clear (same endpoint the wizards use).
      safeFetch(`/api/onboarding/draft?role=${role}`, { method: 'DELETE' }).catch(() => { /* non-critical */ });
    }
  }, [user?.uid, isEmployer, employerApproved, isReferrer, referrerApproved, isSponsor, sponsorApproved]);

  // §1.5(c): toasts never follow the user across navigation. SPA route
  // changes (tab switches, deep links) clear the global toast stack; the
  // 5s auto-dismiss timer still applies within a screen.
  const pathForToastClear = usePath();
  const prevPathForToastRef = useRef(pathForToastClear);
  useEffect(() => {
    if (prevPathForToastRef.current !== pathForToastClear) {
      prevPathForToastRef.current = pathForToastClear;
      clearAllToasts();
    }
  }, [pathForToastClear]);

  // §1.5(b): boot deadline — if the signed-in boot sequence stalls (auth
  // init or profile load never completes), surface the retry affordance
  // instead of an endless "Initializing Secure Gateway" spinner.
  const bootInProgress = !authInitialized || !user || !profile || !authReady;
  useEffect(() => {
    if (!bootInProgress || !user) { setBootStuck(false); return; }
    const t = window.setTimeout(() => setBootStuck(true), 25000);
    return () => window.clearTimeout(t);
  }, [bootInProgress, user]);

  useEffect(() => {
    if (!user) return;

    // Foreground push notification handler (app is open and focused).
    // Permission is requested on user gesture only, via NotificationBanner
    // and NotificationToggle components.
    const unsub = onForegroundMessage((payload) => {
      const title = payload.notification?.title || (import.meta.env.VITE_BRAND_BUSINESS_NAME as string) || 'ISATS';
      const body = payload.notification?.body || '';
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/pwa-192x192.png',
        });
      }
    });
    return () => unsub();
  }, [user]);

  const handleCreatePassword = async (password: string, recoveryEmail?: string) => {
    setPasswordLoading(true);
    setPasswordError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/auth/create-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ password, recoveryEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPasswordError(data.error || 'Failed to create password.');
        setPasswordLoading(false);
        return;
      }

      if (profile && profile.email) {
        sessionStorage.setItem('pending_login_otp_email', profile.email);
      }

      await handleLogout();
    } catch (err: any) {
      setPasswordError('Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  /**
   * In-place role switch — the engine behind the profile-dropdown
   * switcher (the ONE home of role switching on every portal). Validates
   * the target against server truth (same check the selection screen
   * runs), then lands straight in that role's view: no chooser detour,
   * no re-login, no onboarding. The pick is stored as both the session
   * role and the remembered role — exactly like a selection-screen pick
   * — so the last active role persists for the next login (the
   * non-enhanced referrer mandatory-choice rule is untouched: its login
   * effect still skips auto-apply for that tier). Switching to a PENDING
   * referrer lands on its Pending Approval gate via the normal render
   * precedence — validate-role grants the role for any matching doc.
   */
  const switchToRole = async (roleId: string) => {
    // §1.5: acknowledge the click in the SAME frame — the progress overlay
    // renders from this state while validation + the hard reload run, so
    // there is no dead gap between click and feedback.
    setRoleSwitchTarget(roleId);
    if (!multiRoleOptions.some(r => r.id === roleId)) {
      setRoleSwitchTarget(null);
      notifyToast('error', 'That role is not available for your account.');
      return;
    }
    // Shared validation (follow-up Item 1): force-refreshed token +
    // workspace header — a stale token or the default-workspace context
    // made pending org roles fail validation and dead-end here.
    {
      const result = await requestRoleValidation(roleId, (force) => auth.currentUser?.getIdToken(force) ?? Promise.resolve(undefined));
      if (!result.valid) {
        setRoleSwitchTarget(null);
        notifyToast('error', result.error || 'That role is not available for your account.');
        return;
      }
    }
    // User directive: switching role ALWAYS refreshes the browser page —
    // persist the validated pick, then hard-reload to the dashboard so
    // the app boots fresh from server truth. (The previous in-place
    // state swap silently no-opped: refreshProfile re-detected roles
    // from the profile doc and ignored the session selection — G22.)
    switchRoleWithReload(roleId);
  };

  /** The switcher bundle for one dashboard (undefined for single-role users). */
  const roleSwitchFor = (currentRoleId: string): RoleSwitchControl | undefined => {
    // While role data is still resolving, hand hosts a LOADING bundle so
    // the switcher section renders "Loading your roles…" instead of
    // being absent on first open (G15/G17) — it never unmounts back to
    // blank once loaded.
    if (!roleOptionsResolved) {
      return { options: [], currentRoleId, onSelectRole: () => {}, loading: true };
    }
    return multiRoleOptions.length > 1
      ? { options: multiRoleOptions, currentRoleId, onSelectRole: (roleId: string) => { void switchToRole(roleId); } }
      : undefined;
  };

  const handleLogout = async () => {
    setLoading(true);
    setProfileLoadTimeout(false);
    try {
      await signOut(auth);
    } catch (e) {
      console.warn('Standard signout errored:', e);
    }
    setUser(null);
    setProfile(null);
    setIsAdmin(false);
    setIsEmployer(false);
    setIsReferrer(false);
    setIsSponsor(false);
    setSponsorApproved(false);
    setEmployerOnboarded(null);
    setReferrerOnboarded(null);
    setRoleStatusResolved(false);
    roleStatusEverResolvedRef.current = false;
    // PR-13: trust state is per-session — a different account signing in
    // on this browser must start from an unconfirmed slate.
    referrerTrustRef.current = createOrgStatusTrustState();
    employerTrustRef.current = createOrgStatusTrustState();
    if (negativeRecheckTimerRef.current !== null) {
      window.clearTimeout(negativeRecheckTimerRef.current);
      negativeRecheckTimerRef.current = null;
    }
    setReferrerProfileIncomplete(false);
    setReferrerStatus(null);
    setReferrerOrgId(null);
    setEmployerStatus(null);
    setEmployerRejectionReason('');
    setReferrerRejectionReason('');
    setSponsorRejectionReason('');
    setIsSuperAgent(true);
    setSelectedSessionRole(null);
    setMultiRoleOptions([]);
    setRoleOptionsResolved(false);
    setForceDeactivatedBanner(false);
    forcedSelectionAppliedRef.current = false;
    sessionStorage.removeItem('isats_selected_role');
    setAuthReady(false);
    setLoading(false);
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('local_apps_') || key.startsWith('docs_') || key.startsWith('docRequests_') || key.startsWith('comments_') ||
          key === 'all_cached_applications' || key === 'local_audit_logs')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    // §1.2: account-scoped client state leaves with the account — this
    // user's onboarding drafts (all org roles) and remembered role must not
    // bleed into the next account that signs in on this browser.
    const departingUid = user?.uid || profile?.uid;
    if (departingUid) {
      for (const draftRole of ['employer', 'referrer', 'sponsor'] as const) {
        try { localStorage.removeItem(`onboarding:draft:${draftRole}:${departingUid}`); } catch { /* storage unavailable */ }
      }
      try { localStorage.removeItem(rememberedRoleKey(departingUid)); } catch { /* storage unavailable */ }
    }
    try { localStorage.removeItem(REMEMBERED_ROLE_KEY); } catch { /* storage unavailable */ }
    sessionStorage.clear();
  };

  // ── Session revocation: force logout immediately on every device ──────────
  // When an admin/team-member role is revoked server-side, all of the user's
  // sessions die instantly. The server returns 401 SESSION_REVOKED; we also
  // poll a lightweight status endpoint so even an idle user (no other API
  // calls happening) is logged out within a few seconds.
  useEffect(() => {
    if (!user || !auth.currentUser) return;

    let cancelled = false;

    const forceLogout = (reason: string) => {
      if (cancelled) return;
      try {
        notifyToast('warning', reason || 'Your session was revoked. Please sign in again.', 'Session revoked');
      } catch { /* toast layer not critical during teardown */ }
      handleLogout();
    };

    const handleSessionRevoked = (e: Event) => {
      forceLogout((e as CustomEvent).detail || 'Your session was revoked. Please sign in again.');
    };

    const pollSession = async () => {
      if (cancelled || !auth.currentUser) return;
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch('/api/auth/session-status', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          const data = await res.json().catch(() => ({}));
          // Deactivated accounts land on the "Account Inactive" banner — not a
          // generic error, not the dashboard, not onboarding. The server has
          // already revoked the tokens; sign out the local Firebase session
          // and force the banner. The banner stays until explicit sign-out or
          // a fresh sign-in (post-reactivation).
          if (data.error === 'ACCOUNT_DEACTIVATED' || data.deactivated === true) {
            if (!cancelled) {
              setForceDeactivatedBanner(true);
              try { await signOut(auth); } catch { /* already signed out */ }
            }
            return;
          }
          // Any other 401 means the session is no longer valid — revoked,
          // blocked, or removed. Log out immediately on this device.
          forceLogout(data.reason || data.error || 'Your session was revoked. Please sign in again.');
        }
      } catch { /* transient network error — retry on next tick */ }
    };

    window.addEventListener('session:revoked', handleSessionRevoked);
    pollSession();
    const interval = setInterval(pollSession, 15000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('session:revoked', handleSessionRevoked);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, auth.currentUser?.uid]);

  // ── Real-time permission sync listener for active workspace team members ──
  useEffect(() => {
    if (!user || !profile || !isAdmin || !db) return;
    const userDocRef = doc(db, 'users', user.uid);
    const unsub = onSnapshot(userDocRef, async (snap) => {
      if (!snap.exists()) return;
      try {
        const data = await safeFetch('/api/profile/effective-identity');
        if (data?.identity?.permissions) {
          setProfile(prev => {
            if (!prev) return prev;
            if (JSON.stringify(prev.permissions) === JSON.stringify(data.identity.permissions)) return prev;
            return {
              ...prev,
              workspaceRole: data.identity.workspaceRole || prev.workspaceRole,
              permissions: data.identity.permissions,
              applicantScope: data.identity.applicantScope || prev.applicantScope,
            };
          });
        }
      } catch { /* ignore network blips */ }
    }, () => {});
    return () => unsub();
  }, [user?.uid, isAdmin]);

  // ── Effective workspace permissions for team members ─────────────────────
  // The server is the source of truth for what a workspace team member can
  // actually access. Resolve per-tab permissions for the ACTIVE workspace and
  // merge them into the profile so the sidebar + tab gate (hasModuleAccess)
  // never render a module the server would reject. Runs once per sign-in /
  // workspace switch; workspace switches trigger a full page load anyway.
  const effectiveIdentityFetchedRef = useRef<string>('');
  useEffect(() => {
    if (!user || !profile || !isAdmin) return;
    const wsRole = profile.workspaceRole || profile.role || '';
    if (!wsRole) return; // not a workspace team member (applicants/employers/…)
    let cancelled = false;
    (async () => {
      // The active workspace id is persisted by WorkspaceProvider after it
      // loads /api/workspaces/me. On a fresh sign-in the profile mounts before
      // that resolves, so wait briefly for the workspace to be known. Without
      // this the request would target the default workspace (or none) and the
      // member's real server grants would never be merged. Best-effort: once
      // the workspace is known later (or the app reloads on a workspace
      // switch) this effect re-runs with the new cache key.
      const wsId = getActiveWorkspaceId() || 'isats';
      const cacheKey = `${user.uid}:${wsId}:${wsRole}`;
      if (cancelled || effectiveIdentityFetchedRef.current === cacheKey) return;
      try {
        const data = await safeFetch('/api/profile/effective-identity');
        const identity = data?.identity;
        if (cancelled || !identity || typeof identity.permissions !== 'object') return;
        effectiveIdentityFetchedRef.current = cacheKey;
        setProfile(prev => {
          if (!prev) return prev;
          const next: UserProfile = {
            ...prev,
            workspaceRole: identity.workspaceRole || prev.workspaceRole,
            permissions: identity.permissions,
            applicantScope: identity.applicantScope || prev.applicantScope,
          };
          if (next.workspaceRole === prev.workspaceRole
            && JSON.stringify(next.permissions) === JSON.stringify(prev.permissions)
            && next.applicantScope === prev.applicantScope) return prev;
          return next;
        });
      } catch { /* non-critical — hasModuleAccess denies by default */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isAdmin, profile?.uid, profile?.workspaceRole]);

  const updateBanner = (showUpdateBanner && !updateBannerDismissed) ? (
    <div className="relative z-[80] btn-primary text-white text-xs sm:text-sm font-semibold px-4 py-2 flex items-center justify-between shadow-md" role="alert" aria-live="assertive">
      <span className="flex items-center gap-2 min-w-0 truncate">
        <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
        <span className="truncate">A new version of the portal is available.</span>
      </span>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => {
            sessionStorage.setItem('update_reload_at', String(Date.now()));
            if ('serviceWorker' in navigator) {
              let reloaded = false;
              const doReload = () => {
                if (reloaded) return;
                reloaded = true;
                window.location.reload();
              };
              const onControllerChange = () => {
                navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
                doReload();
              };
              navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
              navigator.serviceWorker.getRegistration().then(reg => {
                reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
              });
              setTimeout(doReload, 3000);
            } else {
              window.location.reload();
            }
          }}
          className="px-2.5 py-1 bg-white text-brand rounded-lg text-xs font-bold bg-brand-active transition-all cursor-pointer"
        >
          Refresh Now
        </button>
        <button
          onClick={() => setUpdateBannerDismissed(true)}
          className="p-1 rounded-lg hover:bg-white/20 transition-all cursor-pointer text-white/80 hover:text-white"
          aria-label="Dismiss update banner"
        >
          Later
        </button>
      </div>
    </div>
  ) : null;

  // Early return: if they are on a public booking path, let them view the public scheduling page login-free
  const matchGroup = path.match(/^\/group-schedule\/([a-zA-Z0-9_-]+)$/);
  const matchSchedule = path.match(/^\/schedule\/([a-zA-Z0-9_-]+)$/);
  const matchBookScreening = path.match(/^\/book-screening$/);
  const matchBookAgent = path.match(/^\/(?:book-agent|book)\/([a-zA-Z0-9_-]+)$/);
  const matchSpecialRequest = path.match(/^\/special-request\/([a-zA-Z0-9_-]+)$/);
  const matchPay = path.match(/^\/pay\/([a-zA-Z0-9_-]+)$/);
  const matchQuote = path.match(/^\/quote\/([a-zA-Z0-9_-]+)$/);
  // E-signature links are emailed to external signers who have no portal
  // account — the token IS the credential (see /api/public/esign), so the
  // page must render here, before any auth/workspace gate.
  const matchSign = path.match(/^\/sign\/([a-zA-Z0-9_-]+)$/);
  // Wave 4 legal pages — rendered from the workspace Legal config by host.
  const matchLegal = path.match(/^\/(privacy|terms|refund-policy|cookies|accessibility)$/);
  const matchPrivacyRequest = path.match(/^\/privacy-request$/);
  const matchUnsubscribe = path.match(/^\/unsubscribe$/);
  const isPublicRoute = !!(matchGroup || matchSchedule || matchBookScreening || matchBookAgent || matchSpecialRequest || matchPay || matchQuote || matchSign || matchLegal || matchPrivacyRequest || matchUnsubscribe);

  if (isPublicRoute) {
    return (
      <WorkspaceProvider>
        <GlobalToastLayer />
      <GlobalConfirmLayer />
        <Suspense fallback={<div className="min-h-dvh premium-shell flex items-center justify-center"><div className="premium-loader" /></div>}>
          {matchGroup ? (
            <PublicGroupScheduleView eventId={matchGroup[1]} />
          ) : matchBookScreening ? (
            <PublicScreeningBookingView />
          ) : matchBookAgent ? (
            <PublicAgentBookingView agentUid={matchBookAgent[1]} />
          ) : matchSpecialRequest ? (
            <PublicSpecialRequestView token={matchSpecialRequest[1]} />
          ) : matchPay ? (
            <PublicPaymentView token={matchPay[1]} />
          ) : matchQuote ? (
            <PublicQuoteView token={matchQuote[1]} />
          ) : matchSign ? (
            <PublicSigningView token={matchSign[1]} />
          ) : matchLegal ? (
            <LegalDocumentPage docType={matchLegal[1] === 'refund-policy' ? 'refund' : matchLegal[1] === 'cookies' ? 'cookie' : (matchLegal[1] as 'privacy' | 'terms' | 'accessibility')} />
          ) : matchPrivacyRequest ? (
            <PrivacyRequestPage />
          ) : matchUnsubscribe ? (
            <UnsubscribePage />
          ) : (
            <PublicScheduleView token={matchSchedule![1]} />
          )}
        </Suspense>
      </WorkspaceProvider>
    );
  }

  // "Account Inactive" screen — ONE implementation shared by every
  // deactivation path: the profile-status gate below, the forced
  // session-poll banner, and the confirmed org-status terminal branch.
  const inactiveAccountScreen = (reason?: string) => (
    <div className="min-h-dvh flex flex-col items-center justify-center bg-canvas p-6 font-sans text-text-primary">
      <div className="bg-surface border border-border-subtle shadow-card rounded-3xl p-8 max-w-md w-full text-center space-y-6">
        <div className="w-16 h-16 rounded-full bg-rose-500/10 text-rose-600 flex items-center justify-center mx-auto">
          <Lock className="h-8 w-8" />
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-text-primary">{INACTIVE_ACCOUNT_BANNER.heading}</h2>
          <p className="text-xs text-text-secondary leading-relaxed">
            {INACTIVE_ACCOUNT_BANNER.body}
          </p>
          {reason && (
            <div className="bg-canvas-elevated border border-border-subtle rounded-xl p-3 text-xs text-text-primary italic text-left">
              <strong>Reason:</strong> {reason}
            </div>
          )}
        </div>
        <div className="pt-2 flex flex-col gap-3">
          <a
            href={`mailto:${brand.supportEmail}`}
            className="py-3 btn-primary text-white rounded-xl text-xs font-bold transition flex items-center justify-center gap-2"
          >
            Contact Support ({brand.supportEmail})
          </a>
          <button
            onClick={handleLogout}
            className="py-2.5 bg-surface-pressed hover:bg-canvas-elevated text-text-primary rounded-xl text-xs font-semibold transition cursor-pointer"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );

  // Forced Deactivated Banner: set by the session poll when the server
  // reports the account was deactivated (tokens revoked). Renders ONLY
  // the "Account Inactive" banner — never the login screen, dashboard,
  // onboarding, pending screen, or role screen. It takes precedence over
  // every other gate, INCLUDING the signed-out AuthPage return below
  // (D-18): the poll signs the local Firebase session out in the same
  // breath as it sets this flag, so when this check sat after the !user
  // return the banner was unreachable at fresh sign-in — the user hung
  // indefinitely on AuthPage's "You're signed in. Taking you to your
  // dashboard…" step and the session silently vanished on reload,
  // without ever seeing the inactive notice.
  if (forceDeactivatedBanner) {
    return inactiveAccountScreen();
  }

  // Self-Serve Onboarding §2.2 T-6 / D-0 + D-8: the platform signup
  // route. While auth state is resolving, only the loader renders (no
  // flash of form or notice). Signed-out visitors get the platform
  // signup page. A PRE-EXISTING signed-in session gets the neutral
  // platform-context notice — no navigation into any workspace, no
  // workspace branding or data (D-8). The signup flow's own
  // just-provisioned session (getStartedHandoff) keeps the page
  // mounted so its done step can perform the landing itself.
  if (path === '/get-started') {
    if (!authInitialized) {
      return (
        <div className="min-h-dvh premium-shell flex items-center justify-center">
          <div className="premium-loader" />
        </div>
      );
    }
    if (user && !getStartedHandoff) {
      return (
        <Suspense fallback={<div className="min-h-dvh premium-shell flex items-center justify-center"><div className="premium-loader" /></div>}>
          <GetStartedSignedInNotice />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<div className="min-h-dvh premium-shell flex items-center justify-center"><div className="premium-loader" /></div>}>
        <GetStartedPage onHandoffSessionStart={() => setGetStartedHandoff(true)} />
      </Suspense>
    );
  }

  // Early return: if Firebase has initialized and there's no user, show AuthPage immediately.
  // No loading spinner for unauthenticated users — they see the login page instantly.
  if (authInitialized && !user) {
    return (
      <>
        {simulationMode && user && (
          <SimulationBanner simulatedUser={{ email: simulationMode.email, uid: simulationMode.uid }} />
        )}
        <PwaUpdater />
        {updateBanner}
        <Suspense fallback={<div className="min-h-dvh premium-shell flex items-center justify-center"><div className="premium-loader" /></div>}>
          <AuthPage />
        </Suspense>
      </>
    );
  }

  // Deactivated Account Gate
  // PR-13b (product rule change, 2026-10-13): a suspension sourced by the
  // user's REFERRER org restricts referring only — it must NOT fire this
  // hard gate. The account proceeds to normal routing (restricted
  // referrer dashboard / applicant side fully usable). Employer/sponsor-
  // sourced and unproven suspensions keep the hard inactive screen.
  if (authInitialized && user && profile && authReady && (profile.status === 'deactivated' || profile.status === 'disabled')) {
    const referrerSourced = isReferrerSourcedSuspension({
      profileStatus: profile.status,
      suspendedOrgId: (profile as any).suspendedOrgId,
      referrerStatus,
      referrerOrgId: referrerOrgId || (profile as any).referrerId || null,
    });
    if (!referrerSourced) {
      return inactiveAccountScreen((profile as any).deactivationReason);
    }
  }

  // Role Selection Gate: Force role selection if profile.role is null/empty or if multi-role user has not selected a session role.
  // The option list must be RESOLVED first — never render the screen from a
  // guessed or partial list (intermittent 3-card flash). For referrers the
  // tier (referrerEnhanced) must also have resolved so the mandatory 2-card
  // list is in place rather than the general computation.
  // Follow-up Item 11: the user's SAVED profile, handed to every org
  // wizard so fields the profile already holds (phone, address, city,
  // country) pre-fill instead of being re-typed.
  const savedProfileForWizard = {
    firstName: profile?.firstName || undefined,
    lastName: profile?.lastName || undefined,
    displayName: profile?.displayName || undefined,
    phoneNumber: (profile as any)?.phoneNumber || undefined,
    phoneCountryCode: (profile as any)?.phoneCountryCode || undefined,
    address: (profile as any)?.address || undefined,
    addressLine1: (profile as any)?.addressLine1 || undefined,
    city: (profile as any)?.city || undefined,
    state: (profile as any)?.state || undefined,
    country: (profile as any)?.country || undefined,
    postalCode: (profile as any)?.postalCode || undefined,
  };

  // §1.5: role-switch progress overlay — rendered by every return path a
  // switch can start from (selection screen + main shell). A top progress
  // bar plus a labeled card, so the wait is visibly "going somewhere".
  const roleSwitchOverlay = roleSwitchTarget ? (
    <div className="fixed inset-0 z-[120] flex items-start justify-center pointer-events-none" role="status" aria-live="polite" aria-label="Switching role">
      <div className="absolute top-0 left-0 right-0 h-1 bg-slate-200/70 overflow-hidden">
        <div className="h-full w-1/3 bg-brand-primary role-switch-progress" />
      </div>
      <div className="mt-24 flex items-center gap-3 rounded-2xl bg-white/95 backdrop-blur px-5 py-3.5 shadow-2xl ring-1 ring-slate-200">
        <div className="premium-loader" aria-hidden="true" />
        <p className="text-sm font-semibold text-slate-800">
          Switching to {multiRoleOptions.find(r => r.id === roleSwitchTarget)?.title || 'your dashboard'}…
        </p>
      </div>
    </div>
  ) : null;

  // §1.3: wizard escape hatch — the standard profile menu (role switch +
  // sign out) floats over every onboarding wizard so a dual-role user is
  // never trapped in a wizard with no way back to their other role. The
  // wizard roots are full-viewport (h-dvh), so the menu overlays instead
  // of reflowing them. UserMenu is consumed as-is (not modified).
  const wizardEscapeHatch = (currentRoleId: string) => (
    <div className="fixed top-3 right-3 z-[95] rounded-2xl bg-white/90 backdrop-blur px-1 py-0.5 shadow-lg ring-1 ring-slate-200/80">
      <UserMenu profile={profile} email={profile?.email} onLogout={handleLogout} roleSwitch={roleSwitchFor(currentRoleId)} />
    </div>
  );

  // PR-13b: the "Loading your roles" gate may only wait for data the
  // next screen needs (see roleScreenReady in accountStatus.ts). A user
  // whose role is already settled (single role, or a stored session
  // choice — e.g. direct /dashboard navigation) must never be trapped
  // on this spinner by a lagging referrer-tier fetch.
  const roleSelectionNeeded = !profile?.role || (multiRoleOptions.length > 1 && !selectedSessionRole);
  const roleScreenReadyNow = roleScreenReady({
    roleOptionsResolved,
    roleSelectionNeeded,
    isReferrer,
    referrerTierResolved: referrerEnhanced !== null,
    roleStatusFailed,
  });
  if (authInitialized && user && profile && authReady && !roleScreenReadyNow) {
    return (
      <div className="min-h-dvh premium-shell flex items-center justify-center" role="status" aria-live="polite" aria-label="Loading your roles">
        <div className="premium-loader" />
      </div>
    );
  }
  if (authInitialized && user && profile && authReady && (!profile.role || (multiRoleOptions.length > 1 && !selectedSessionRole))) {
    const handleSelectRole = async (roleId: string) => {
      // §1.5: acknowledge the click in the SAME frame (progress overlay).
      setRoleSwitchTarget(roleId);
      // Never let a tampered client select a role the user was not offered —
      // the available options are computed from server-issued signals only.
      if (!multiRoleOptions.some((r) => r.id === roleId)) {
        setRoleSwitchTarget(null);
        notifyToast('error', 'That role is not available for your account.');
        return;
      }
      // Server-truth validation: confirm the target role against the user's
      // ACTUAL roles/memberships before switching. Tampered selections (e.g.
      // 'team' for a non-team user) are rejected with 403. Shared helper
      // (follow-up Item 1): force-refreshed token + workspace header.
      {
        const result = await requestRoleValidation(roleId, (force) => auth.currentUser?.getIdToken(force) ?? Promise.resolve(undefined));
        if (!result.valid) {
          setRoleSwitchTarget(null);
          notifyToast('error', result.error || 'That role is not available for your account.');
          return;
        }
      }
      if (!profile.role) {
        try {
          const res = await fetch('/api/auth/select-role', {
            method: 'POST',
            headers: await roleAuthHeaders((force) => auth.currentUser?.getIdToken(force) ?? Promise.resolve(undefined)),
            body: JSON.stringify({ role: roleId }),
          });
          const data = await res.json();
          if (!res.ok) {
            setRoleSwitchTarget(null);
            notifyToast('error', data.error || 'Failed to set role.');
            return;
          }
          if (data.customToken) {
            await signInWithCustomToken(auth, data.customToken);
          }
          // Role choice made: persist + hard reload to the dashboard
          // (same ending as every role switch — see switchToRole).
          switchRoleWithReload(roleId);
          return;
        } catch {
          setRoleSwitchTarget(null);
          notifyToast('error', 'Network error. Please try again.');
        }
      } else {
        // Persist + hard reload to the dashboard (see switchToRole).
        switchRoleWithReload(roleId);
      }
    };

    return (
      <>
        {roleSwitchOverlay}
        <RoleSelectionScreen
          availableRoles={multiRoleOptions}
          displayName={sanitizeNamePart(profile.displayName) || joinPersonName([profile.firstName, profile.lastName]) || profile.email}
          email={profile.email}
          onSelectRole={handleSelectRole}
          onLogout={handleLogout}
        />
      </>
    );
  }

  // (The forced deactivated banner is rendered earlier — it must win
  // over the signed-out AuthPage return; see the D-18 note above.)

  // Loading state: show while Firebase is initializing or profile is being fetched.
  if (!authInitialized || !user || !profile || !authReady) {
    return (
      <>
        {simulationMode && user && (
          <SimulationBanner simulatedUser={{ email: simulationMode.email, uid: simulationMode.uid }} />
        )}
        <PwaUpdater />
        {updateBanner}
        <div className="min-h-dvh premium-shell flex flex-col items-center justify-center space-y-5 font-sans text-slate-900 animate-fade-in" role="status" aria-live="polite">
          {profileLoadError || bootStuck ? (
            <>
              <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center ring-1 ring-red-200" aria-hidden="true">
                <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
              </div>
              <div className="text-center space-y-2 max-w-xs">
                <p className="text-sm font-bold text-slate-900">Failed to load profile</p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  A temporary error occurred while loading your account. This usually resolves on retry.
                </p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-2.5 btn-primary  text-white rounded-xl text-sm font-semibold transition-all shadow-lg shadow-blue-600/20 cursor-pointer active:scale-[0.98]"
              >
                Tap to Retry
              </button>
            </>
          ) : (
            <>
              <div className="premium-loader" aria-hidden="true"></div>
              <p className="text-xs text-slate-700 font-black uppercase tracking-[0.18em] font-display">
                {authInitialized ? 'Loading your profile...' : 'Initializing Secure Gateway...'}
              </p>
            </>
          )}
        </div>
      </>
    );
  }

  // PROFILE GATE: enforce profile completion before dashboard access (skip for employers and referrers — they complete during sign-up).
  // If profileCompleted is undefined (new user), wait up to 3s for Firestore to load,
  // but skip the wait if docJustCreated (we just created the doc — it's definitely incomplete).
  // Workspace team members (isAdmin) never go through the applicant profile gate.
  if (profile.profileCompleted === undefined && !isAdmin && !isEmployer && !isReferrer && !isSponsor && !profileLoadTimeout) {
    return (
      <>
        <PwaUpdater />
        {updateBanner}
        <div className="min-h-dvh premium-shell flex flex-col items-center justify-center space-y-5 font-sans text-slate-900 animate-fade-in" role="status" aria-live="polite">
          <div className="premium-loader" aria-hidden="true"></div>
          <p className="text-xs text-slate-700 font-black uppercase tracking-[0.18em] font-display">Loading profile...</p>
        </div>
      </>
    );
  }

  // ── FORCE PASSWORD CREATION GATE ────────────────────────────────────
  if (profile && profile.hasPassword === false) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-slate-50 p-6 sm:p-8 font-sans">
        <PwaUpdater />
        {updateBanner}
        <div className="w-full max-w-sm space-y-6 premium-surface rounded-[2rem] border border-white/70 p-6 sm:p-8 shadow-2xl bg-white text-center">
          <div className="space-y-1">
            <div className="mx-auto w-fit p-3 bg-blue-50 rounded-2xl mb-3 shadow-inner ring-1 ring-blue-100">
              <Lock className="h-7 w-7 text-blue-600" aria-hidden="true" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Create a Password</h2>
            <p className="text-xs text-slate-600 leading-normal">
              To keep your account secure, you must create a password before continuing.
            </p>
          </div>

          {passwordError && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-xs font-bold px-4 py-3 rounded-2xl shadow-sm animate-slide-down">
              {passwordError}
            </div>
          )}

          <PasswordSetForm
            onSubmit={handleCreatePassword}
            submitLabel="Create Password"
            loading={passwordLoading}
            showRecoveryEmail={true}
          />

          <div className="pt-2 border-t border-slate-100">
            <button
              onClick={handleLogout}
              className="text-xs text-slate-500 hover:text-slate-800 font-semibold cursor-pointer transition"
            >
              Sign Out & Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  const userHasCompletedProfile =
    profile.profileCompleted === true ||
    !!profile.firstName ||
    !!(profile as any).personalDetails?.firstName ||
    !!profile.displayName;

  if (!isAdmin && !isEmployer && !isReferrer && !isSponsor && !userHasCompletedProfile) {
    return (
      <>
        <PwaUpdater />
        {updateBanner}
        <Suspense fallback={<div className="min-h-dvh premium-shell flex items-center justify-center"><div className="premium-loader" /></div>}>
          <ProfileCompletionForm
            email={profile.email}
            existingData={profile}
            onComplete={() => setProfileRefreshKey(k => k + 1)}
            onCancel={handleLogout}
          />
        </Suspense>
      </>
    );
  }

  // ── Verification gate moved to Submit step ──────────────────────────
  // Verification is no longer required at dashboard entry. Applicants can
  // freely browse and fill out applications. The gate only triggers when
  // they click "Submit" (handled inside ApplicantFlow).
  // A banner gently reminds unverified users to verify early.
  const isApplicant = !isAdmin && !isEmployer && !isReferrer && !isSponsor;
  const showVerificationBanner = isApplicant &&
    profile.didit?.status !== 'approved';

  // Enhanced referrers get Team/Training/Marketing/Events/Commissions/Performance.
  // Non-enhanced referrers must never see those nav items (sidebar or mobile).
  // This MUST come from the server (referrer doc's enhancedPermission via
  // /api/referrer/onboard-status) — never from isSuperAgent (every org owner
  // is a super agent regardless of tier) or legacy profile fields.
  const isEnhancedReferrer = isReferrer && referrerEnhanced === true;

  // Pending-review precedence: a submitted organisation awaiting review must
  // land on PendingApproval on every session resume — even if the stored
  // profile status looks incomplete. Without this, re-login can bounce a
  // submitted user back into the wizard (or a password step) as if the
  // submission never happened.
  const employerPendingReview = isOrgPendingReview({
    onboarded: employerOnboarded,
    approved: employerApproved,
    profileIncomplete: false,
    orgSetupRequired: (profile as any)?.orgSetupRequired === true,
    roleStatus: employerStatus,
  });
  const referrerPendingReview = isOrgPendingReview({
    onboarded: referrerOnboarded,
    approved: referrerApproved,
    profileIncomplete: referrerProfileIncomplete,
    orgSetupRequired: (profile as any)?.orgSetupRequired === true,
    roleStatus: referrerStatus,
  });
  // PR-13b: restricted referrer (deactivated referring capability) —
  // routes to the read-only dashboard, bypassing wizard/pending/terminal.
  const referrerRestricted = isReferrerRestricted({ onboarded: referrerOnboarded, status: referrerStatus });
  const sponsorPendingReview = isOrgPendingReview({
    onboarded: !!(profile as any)?.sponsorId,
    approved: sponsorApproved,
    profileIncomplete: false,
    orgSetupRequired: (profile as any)?.orgSetupRequired === true,
    roleStatus: (profile as any)?.status === 'rejected' ? 'rejected' : undefined,
  });

  const sidebarTabs: TabConfig[] = isAdmin
    ? // Owner-approved redesign (Oct 2026): flat everyday pages, one Jobs
      // entry fronting the Jobs page tab bar (Job List / Offers /
      // Invitations), collapsible Templates / Organization / Orchestration
      // groups, account entries at the bottom — see lib/adminNavStructure.
      getAdminSidebarTabs(isPlatformAdmin).filter(tab =>
        tab.id === 'jobs'
          ? // The Jobs entry also fronts Offers + Invitations now, so a
            // member who could reach either before keeps a way in.
            hasModuleAccess('jobs', profile, isPlatformAdmin) ||
            hasModuleAccess('offers', profile, isPlatformAdmin) ||
            hasModuleAccess('invitations', profile, isPlatformAdmin)
          : hasModuleAccess(tab.id, profile, isPlatformAdmin))
    : isEmployer
    ? [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, category: 'Main' },
        { id: 'applicants', label: 'Applicants', icon: Users, category: 'Main' },
        { id: 'favorites', label: 'Saved Candidates', icon: Bookmark, category: 'Main' },
        { id: 'talent-pool', label: 'Talent Pool', icon: Users, category: 'Main' },
        { id: 'jobs', label: 'Jobs', icon: Briefcase, category: 'Main' },
        { id: 'messages', label: 'Messages', icon: MessageCircle, category: 'Engagement' },
        { id: 'invitations', label: 'Job Invitations', icon: Mail, category: 'Engagement' },
        { id: 'special-requests', label: 'Special Requests', icon: ClipboardList, category: 'Engagement' },
        { id: 'events', label: 'Events', icon: CalendarDays, category: 'Engagement' },
        { id: 'support', label: 'Support', icon: LifeBuoy, category: 'Engagement' },
        { id: 'agents', label: 'Employer Team', icon: Users, category: 'Company' },
        { id: 'profile', label: 'Company Profile', icon: Home, category: 'Company' },
      // EMP-07: Employer Team is super-agent only — the referrer list
      // below tier-filters its restricted entries the same way. Members
      // without access no longer see an entry that only bounces them
      // back to the dashboard; EmployerDashboard's own gate stays as
      // defence in depth for direct navigation.
      ].filter(tab => isSuperAgent || tab.id !== 'agents')
    : isReferrer
    ? [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, category: 'Overview' },
        { id: 'candidates', label: 'Candidates', icon: Users, category: 'Recruitment' },
        { id: 'jobs', label: 'Job Board', icon: Briefcase, category: 'Recruitment' },
        { id: 'referral-links', label: 'Referral Links', icon: Link, category: 'Recruitment' },
        { id: 'inbox', label: 'Inbox', icon: MessageCircle, category: 'Recruitment' },
        { id: 'invitations', label: 'Invitations', icon: Mail, category: 'Recruitment' },
        { id: 'special-requests', label: 'Special Requests', icon: ClipboardList, category: 'Recruitment' },
        { id: 'commissions', label: isEnhancedReferrer ? 'Commissions' : 'My Commissions', icon: DollarSign, category: 'Performance' },
        { id: 'performance', label: 'Performance', icon: BarChart3, category: 'Performance' },
        { id: 'training', label: 'Training', icon: BookOpen, category: 'Academy' },
        { id: 'marketing', label: 'Marketing', icon: Download, category: 'Academy' },
        { id: 'my-events', label: 'My Events', icon: Calendar, category: 'Events' },
        { id: 'calendar', label: 'Calendar', icon: CalendarDays, category: 'Events' },
        { id: 'support', label: 'Support', icon: LifeBuoy, category: 'Recruitment' },
        { id: 'agents', label: 'Referrer Team', icon: Users, category: 'Team' },
        { id: 'profile', label: 'Profile Settings', icon: Settings, category: 'Account' },
      ].filter(tab => {
        if (!isEnhancedReferrer) {
          // Non-Enhanced Referrers MUST NOT have Agents/Team, Training, Marketing, Events.
          // Support IS available to all referrers.
          // Commissions STAYS visible as read-only "My Commissions" (own data only);
          // performance analytics remain enhanced-only.
          return tab.id !== 'performance' && tab.id !== 'agents' && tab.id !== 'marketing' && tab.id !== 'training' && tab.id !== 'my-events' && tab.id !== 'calendar' && tab.id !== 'events';
        }
        return true;
      })
    : isSponsor
    ? [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, category: 'Casework' },
        { id: 'queue', label: 'Case Review', icon: ClipboardList, category: 'Casework' },
        { id: 'participants', label: 'Participants', icon: Users, category: 'Casework' },
        { id: 'employers', label: 'Employers', icon: Building2, category: 'Casework' },
        { id: 'training', label: 'Training Plans', icon: BookOpen, category: 'Compliance & Ops' },
        { id: 'compliance', label: 'Compliance', icon: BadgeCheck, category: 'Compliance & Ops' },
        { id: 'monitoring', label: 'Monitoring', icon: Radar, category: 'Compliance & Ops' },
        { id: 'communications', label: 'Communications', icon: MessageSquare, category: 'Compliance & Ops' },
        { id: 'evaluations', label: 'Evaluations', icon: Star, category: 'Compliance & Ops' },
        { id: 'documents', label: 'Documents', icon: FileText, category: 'Compliance & Ops' },
        { id: 'special-requests', label: 'Special Requests', icon: ClipboardList, category: 'Compliance & Ops' },
        { id: 'reports', label: 'Reports', icon: BarChart3, category: 'Insights & Risk' },
        { id: 'analytics', label: 'Analytics', icon: PieChart, category: 'Insights & Risk' },
        { id: 'risk', label: 'Risk Center', icon: ShieldAlert, category: 'Insights & Risk' },
        { id: 'exports', label: 'Exports', icon: Download, category: 'Insights & Risk' },
        { id: 'activity', label: 'Activity Logs', icon: Clock, category: 'Insights & Risk' },
        { id: 'support', label: 'Support', icon: LifeBuoy, category: 'Management' },
        { id: 'agents', label: 'Sponsor Team', icon: Users, category: 'Management' },
        { id: 'profile', label: 'Settings', icon: Settings, category: 'Management' },
      ]
    : [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'application', label: 'Application', icon: FileText },
        { id: 'events', label: 'Events', icon: Calendar },
        { id: 'jobs', label: 'Jobs', icon: Briefcase },
        { id: 'support', label: 'Inbox', icon: LifeBuoy },
        { id: 'profile', label: 'Profile', icon: UserCircle },
      ];

  const mobileTabs: TabConfig[] = isAdmin
    ? [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'batch', label: 'Batch', icon: Layers },
        { id: 'my-events', label: 'My Events', icon: Calendar },
        { id: 'jobs', label: 'Jobs', icon: Briefcase },
        { id: 'users', label: 'Users', icon: Users },
      ].filter(tab => hasModuleAccess(tab.id, profile, isPlatformAdmin))
    : isEmployer
    ? [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'jobs', label: 'Jobs', icon: Briefcase },
        { id: 'applicants', label: 'Applicants', icon: Users },
        { id: 'agents', label: 'Employer Team', icon: Users },
        { id: 'profile', label: 'Company Profile', icon: Home },
      // EMP-07: same super-agent gate as the sidebar list above, so the
      // bottom bar never offers Employer Team to a member who cannot
      // open it (bar + More overflow still equals the sidebar set:
      // both lists drop the entry together).
      ].filter(tab => isSuperAgent || tab.id !== 'agents')
    : isReferrer
    ? [
        // §2.7 mobile parity: the bar keeps the five primary surfaces; every
        // other sidebar surface (Referral Links, Invitations, My Commissions,
        // …) is reachable through the BottomNav "More" sheet — see
        // referrerMobileOverflowTabs below, derived from sidebarTabs so the
        // enhanced/non-enhanced filtering is identical on both.
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'candidates', label: 'Candidates', icon: Users },
        { id: 'jobs', label: 'Jobs', icon: Briefcase },
        { id: 'inbox', label: 'Inbox', icon: MessageCircle },
        { id: 'profile', label: 'Profile', icon: Settings },
      ]
    : isSponsor
    ? [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'queue', label: 'Cases', icon: ClipboardList },
        { id: 'participants', label: 'Participants', icon: Users },
        { id: 'risk', label: 'Risk', icon: ShieldAlert },
        { id: 'exports', label: 'Exports', icon: Download },
        { id: 'profile', label: 'Settings', icon: Settings },
      ]
    : [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'application', label: 'Application', icon: FileText },
        { id: 'events', label: 'Events', icon: Calendar },
        { id: 'jobs', label: 'Jobs', icon: Briefcase },
        { id: 'support', label: 'Inbox', icon: LifeBuoy },
        { id: 'profile', label: 'Profile', icon: UserCircle },
      ];

  // §2.7: referrer mobile overflow = every sidebar surface not already in
  // the bottom bar. sidebarTabs is already tier-filtered for referrers, so
  // the More sheet can never offer a surface the role cannot open.
  const REFERRER_MOBILE_PRIMARY = ['dashboard', 'candidates', 'jobs', 'inbox', 'profile'];
  const referrerMobileOverflowTabs: TabConfig[] = isReferrer
    ? sidebarTabs.filter(tab => !REFERRER_MOBILE_PRIMARY.includes(tab.id))
    : [];

  // PR-5: employer + sponsor mobile parity — the same §2.7 pattern. The
  // primary lists below mirror exactly the tab ids in each role's
  // bottom bar (mobileTabs above); every remaining sidebar surface
  // joins the BottomNav "More" sheet instead of being unreachable on
  // phones (employer bar: Dashboard, Jobs, Applicants, Employer Team,
  // Company Profile — so the employer's overflow is Talent Pool,
  // Messages, Invitations, Special Requests, Events, Support and Saved
  // Candidates; sponsor: Training, Compliance, Monitoring,
  // Communications, Evaluations, Documents, Reports, Analytics,
  // Activity, Team, Profile).
  // EMP-04: the employer primary list previously named talent-pool and
  // invitations instead of the bar's actual agents/profile entries, so
  // Talent Pool and Invitations were in neither the bar nor the More
  // sheet — unreachable on phones — while Employer Team and Company
  // Profile appeared in both. tests/employerNavigationTruth.test.ts
  // pins bar + overflow = sidebar for the employer role.
  const EMPLOYER_MOBILE_PRIMARY = ['dashboard', 'jobs', 'applicants', 'agents', 'profile'];
  const employerMobileOverflowTabs: TabConfig[] = isEmployer
    ? sidebarTabs.filter(tab => !EMPLOYER_MOBILE_PRIMARY.includes(tab.id))
    : [];
  const SPONSOR_MOBILE_PRIMARY = ['dashboard', 'queue', 'participants', 'employers', 'risk', 'exports'];
  const sponsorMobileOverflowTabs: TabConfig[] = isSponsor
    ? sidebarTabs.filter(tab => !SPONSOR_MOBILE_PRIMARY.includes(tab.id))
    : [];

  return (
    <WorkspaceProvider>
    <>
      {/* Single global notification layer — portaled to document.body so it is
          never trapped inside a page layout's stacking context. */}
      <GlobalToastLayer />
      <GlobalConfirmLayer />
      {roleSwitchOverlay}
      {simulationMode && user && (
        <SimulationBanner simulatedUser={{ email: simulationMode.email, uid: simulationMode.uid }} />
      )}
      <PwaUpdater />
      {updateBanner}
      <PwaInstallBanner />
      {showVerificationBanner && (
        <VerificationBanner
          userProfile={profile}
          onStatusChange={() => setProfileRefreshKey(k => k + 1)}
        />
      )}
      {/* Pending workspace-team invitations for the signed-in user in THIS
          workspace's context (self-gating: renders nothing when signed out
          or when no invitation is pending). */}
      <WorkspaceTeamInvitePrompt />
      {/* Org access restriction (S4): full-screen restriction message
          for restricted org members, limited outstanding-requests view
          for the designated main user; renders nothing otherwise. */}
      <OrgRestrictionGate />
      {/* FIX 2026-10-07: /accept-invitation must render OUTSIDE WorkspaceGate.
          The gate's useWorkspaces() hook interferes with the invitation flow,
          causing crashes when invitees open the link. */}
      {typeof window !== 'undefined' && window.location.pathname === '/accept-invitation' ? (
        <ErrorBoundary>
          <Suspense fallback={<div className="min-h-dvh premium-shell flex items-center justify-center"><div className="premium-loader" /></div>}>
            <AcceptOrgInvitation />
          </Suspense>
        </ErrorBoundary>
      ) : (
      <WorkspaceGate onLogout={handleLogout}>
      <ErrorBoundary>
        <Suspense fallback={<ShellSkeleton />}>
          {/* Public scheduling page — no auth required */}
          {(() => {
            const currentPath = window.location.pathname;
            // Note: /accept-invitation is handled OUTSIDE WorkspaceGate (see above)
            const matchGroup = currentPath.match(/^\/group-schedule\/([a-zA-Z0-9_-]+)$/);
            if (matchGroup) {
              return <PublicGroupScheduleView eventId={matchGroup[1]} />;
            }
            const matchSpecial = currentPath.match(/^\/special-request\/([a-zA-Z0-9_-]+)$/);
            if (matchSpecial) {
              return <PublicSpecialRequestView token={matchSpecial[1]} />;
            }
            const matchPayPath = currentPath.match(/^\/pay\/([a-zA-Z0-9_-]+)$/);
            if (matchPayPath) {
              return <PublicPaymentView token={matchPayPath[1]} />;
            }
            const matchQuotePath = currentPath.match(/^\/quote\/([a-zA-Z0-9_-]+)$/);
            if (matchQuotePath) {
              return <PublicQuoteView token={matchQuotePath[1]} />;
            }
            // /sign/:token is handled by the early public-route block
            // above (login-free for emailed signers) — no inner route.
            const match = currentPath.match(/^\/schedule\/([a-zA-Z0-9_-]+)$/);
            if (match) {
              return <PublicScheduleView token={match[1]} />;
            }
            return null;
          })() || (isAdmin && !sessionStorage.getItem('pending_login_otp_email') ? (
            <div className="app-shell">
              <div className="app-canvas">
                <CollapsibleSidebar tabs={sidebarTabs} activeTab={JOBS_AREA_TAB_IDS.includes(activeNavTab) ? 'jobs' : activeNavTab} onTabChange={handleNavChange} onLogout={handleLogout} />
                <div className="flex-1 min-w-0 h-full min-h-0 flex flex-col">
                  <AdminDashboard onLogout={handleLogout} adminEmail={profile.email} userProfile={profile} isPlatformAdmin={isPlatformAdmin} />
                  <BottomNav tabs={mobileTabs} activeTab={activeNavTab} onTabChange={handleNavChange} />
                </div>
              </div>
            </div>
          ) : !orgRoleBranchesReady({ isEmployer, isReferrer, isSponsor, roleStatusResolved, employerApproved, referrerApproved, roleStatusFailed }) ? (
            // Post-auth route gate: suspend the org-role transition on a
            // loader until the role-status check resolves for this session.
            // Rendering the branches from stale (null) state would flash the
            // onboarding wizard for submitted users awaiting review.
            <ShellSkeleton />
          ) : roleStatusFailed ? (
            // Role-status check failed (network/server error). The status is
            // UNKNOWN — never fall through to onboarding, dashboard, or role
            // screens. Keep a loader with a retry affordance.
            <div className="min-h-dvh premium-shell flex flex-col items-center justify-center gap-4 p-6" role="alert" aria-live="assertive">
              <div className="premium-loader" />
              <p className="text-sm text-slate-600 text-center max-w-sm">
                We couldn't load your account status. Please check your connection and try again.
              </p>
              <button
                onClick={() => { setRoleStatusFailed(false); setRoleStatusResolved(false); checkRoleStatus(); }}
                className="px-5 py-2.5 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-xl text-sm font-semibold transition cursor-pointer"
              >
                Retry
              </button>
            </div>
          ) : (isEmployer && isOrgTerminalInactiveStatus(employerStatus)) || (isSponsor && isOrgTerminalInactiveStatus(sponsorStatus)) ? (
            // Terminal inactive org (PR-13 fold-in, D-18): the server's
            // confirmed status for the active role is deactivated /
            // suspended — a terminal state with its own screen, never
            // the wizard, never the pending screen, never the dashboard.
            // (The user-doc gate above catches the common deactivation
            // path, where the engine stamps users/{uid}.status; this
            // branch covers orgs whose status round reports the terminal
            // state without that stamp — team members, legacy writes.)
            // PR-13b: REFERRERS are deliberately absent here — a
            // deactivated referrer is restricted, not locked out, and
            // routes to the read-only dashboard below (rule change,
            // 2026-10-13). Employer/sponsor keep the terminal screen.
            inactiveAccountScreen((profile as any).deactivationReason)
          ) : isEmployer && orgOnboardingGate({ approved: employerApproved, pendingReview: employerPendingReview, needsOnboarding: profile.status === 'incomplete' || employerOnboarded === false || profile.orgSetupRequired === true, roleStatus: employerStatus }) ? (
            <>{wizardEscapeHatch('employer')}<EmployerOnboarding userId={profile.uid} userEmail={profile.email} initialFirstName={profile.firstName || ''} initialLastName={profile.lastName || ''} savedProfile={savedProfileForWizard} onComplete={() => { employerTrustRef.current = applyOrgStatusRound(employerTrustRef.current, 'positive', Date.now()); setEmployerOnboarded(true); setProfileRefreshKey(k => k + 1); }} onLogout={handleLogout} /></>
          ) : isEmployer && employerApproved === false ? (
            <PendingApproval
              role="employer"
              status={employerInfo?.status === 'rejected' || profile.status === 'rejected' ? 'rejected' : 'pending'}
              rejectionReason={employerRejectionReason || (employerInfo as any)?.rejectionReason || (profile as any)?.rejectionReason}
              onLogout={handleLogout}
              onRefresh={checkRoleStatus}
              onResubmit={() => { setEmployerOnboarded(false); setProfileRefreshKey(k => k + 1); }}
              userId={profile.uid}
              onSwitchRole={multiRoleOptions.some(r => r.id === 'applicant') ? () => { void switchToRole('applicant'); } : undefined}
              switchRoleLabel="Back to my application"
            />
          ) : isEmployer && !sessionStorage.getItem('pending_login_otp_email') ? (
            <TeamSelectionGate role="employer" onLogout={handleLogout}>
            <div className="app-shell">
              <div className="app-canvas">
                <CollapsibleSidebar tabs={sidebarTabs} activeTab={activeNavTab} onTabChange={handleNavChange} onLogout={handleLogout} />
                <div className="flex-1 min-w-0 h-full min-h-0 flex flex-col">
                  <EmployerDashboard onLogout={handleLogout} userEmail={profile.email} userId={profile.uid} employerInfo={employerInfo} userProfile={profile} isApproved={employerApproved === true} roleSwitch={roleSwitchFor('employer')} />
                  <BottomNav tabs={mobileTabs} activeTab={activeNavTab} onTabChange={handleNavChange} overflowTabs={employerMobileOverflowTabs} />
                </div>
              </div>
            </div>
            </TeamSelectionGate>
          ) : isReferrer && orgOnboardingGate({ approved: referrerApproved, pendingReview: referrerPendingReview, needsOnboarding: profile.status === 'incomplete' || referrerOnboarded === false || profile.orgSetupRequired === true || (referrerProfileIncomplete && referrerStatus !== 'rejected'), roleStatus: referrerStatus }) ? (
            <>{wizardEscapeHatch('referrer')}<ReferrerOnboarding userId={profile.uid} userEmail={profile.email} initialFirstName={profile.firstName || ''} initialLastName={profile.lastName || ''} savedProfile={savedProfileForWizard} onComplete={() => { referrerTrustRef.current = applyOrgStatusRound(referrerTrustRef.current, 'positive', Date.now()); setReferrerOnboarded(true); setReferrerProfileIncomplete(false); setReferrerStatus('pending'); setProfileRefreshKey(k => k + 1); }} onLogout={handleLogout} /></>
          ) : isReferrer && referrerApproved === false && !referrerRestricted ? (
            <PendingApproval
              role="referrer"
              status={referrerStatus === 'rejected' || profile.status === 'rejected' ? 'rejected' : 'pending'}
              rejectionReason={referrerRejectionReason || (profile as any)?.rejectionReason}
              onLogout={handleLogout}
              onRefresh={checkRoleStatus}
              onResubmit={() => { setReferrerOnboarded(false); setReferrerProfileIncomplete(true); setProfileRefreshKey(k => k + 1); }}
              userId={profile.uid}
              isEnhancedReferrer={isEnhancedReferrer}
              onSwitchRole={multiRoleOptions.some(r => r.id === 'applicant') ? () => { void switchToRole('applicant'); } : undefined}
              switchRoleLabel="Back to my application"
            />
          ) : isReferrer && !sessionStorage.getItem('pending_login_otp_email') ? (
            <TeamSelectionGate role="referrer" onLogout={handleLogout}>
            <div className="app-shell">
              <div className="app-canvas">
                <CollapsibleSidebar tabs={sidebarTabs} activeTab={activeNavTab} onTabChange={handleNavChange} onLogout={handleLogout} />
                <div className="flex-1 min-w-0 h-full min-h-0 flex flex-col">
                  <ReferrerDashboard onLogout={handleLogout} userEmail={profile.email} userId={profile.uid} userProfile={profile} isApproved={referrerApproved === true} restricted={referrerRestricted} roleSwitch={roleSwitchFor('referrer')} />
                  <BottomNav tabs={mobileTabs} activeTab={activeNavTab} onTabChange={handleNavChange} overflowTabs={referrerMobileOverflowTabs} />
                </div>
              </div>
            </div>
            </TeamSelectionGate>
          ) : isSponsor && orgOnboardingGate({ approved: sponsorApproved, pendingReview: sponsorPendingReview, needsOnboarding: profile.status === 'incomplete' || !profile.sponsorId || profile.orgSetupRequired === true, roleStatus: sponsorStatus }) ? (
            <>{wizardEscapeHatch('sponsor')}<SponsorOnboarding userId={profile.uid} userEmail={profile.email} initialFirstName={profile.firstName || ''} initialLastName={profile.lastName || ''} savedProfile={savedProfileForWizard} onComplete={() => { setProfileRefreshKey(k => k + 1); }} onLogout={handleLogout} /></>
          ) : isSponsor && sponsorApproved === false ? (
            <PendingApproval
              role="sponsor"
              status={sponsorStatus === 'rejected' || profile.status === 'rejected' ? 'rejected' : 'pending'}
              rejectionReason={sponsorRejectionReason || (profile as any)?.rejectionReason}
              onLogout={handleLogout}
              onRefresh={checkRoleStatus}
              onResubmit={() => { setProfileRefreshKey(k => k + 1); }}
              userId={profile.uid}
              onSwitchRole={multiRoleOptions.some(r => r.id === 'applicant') ? () => { void switchToRole('applicant'); } : undefined}
              switchRoleLabel="Back to my application"
            />
          ) : isSponsor && !sessionStorage.getItem('pending_login_otp_email') ? (
            <TeamSelectionGate role="sponsor" onLogout={handleLogout}>
            <div className="app-shell">
              <div className="app-canvas">
                <CollapsibleSidebar tabs={sidebarTabs} activeTab={activeNavTab} onTabChange={handleNavChange} onLogout={handleLogout} />
                <div className="flex-1 min-w-0 h-full min-h-0 flex flex-col">
                  <SponsorDashboard onLogout={handleLogout} userEmail={profile.email} userId={profile.uid} userProfile={profile} roleSwitch={roleSwitchFor('sponsor')} />
                  <BottomNav tabs={mobileTabs} activeTab={activeNavTab} onTabChange={handleNavChange} overflowTabs={sponsorMobileOverflowTabs} />
                </div>
              </div>
            </div>
            </TeamSelectionGate>
          ) : becomeReferrerWizard ? (
            // "Become a referrer": the SAME referrer onboarding wizard +
            // approval flow as new referrer signups, launched by an existing
            // applicant. Stepper/progress UI lives only inside this
            // new-referrer creation state — never on pending/login/role
            // screens or the dashboards.
            <>{wizardEscapeHatch('applicant')}<ReferrerOnboarding
              userId={profile.uid}
              userEmail={profile.email}
              initialFirstName={profile.firstName || ''}
              initialLastName={profile.lastName || ''}
              savedProfile={savedProfileForWizard}
              onComplete={() => {
                setBecomeReferrerWizard(false);
                referrerTrustRef.current = applyOrgStatusRound(referrerTrustRef.current, 'positive', Date.now());
                setReferrerOnboarded(true);
                setReferrerProfileIncomplete(false);
                setReferrerStatus('pending');
                setProfileRefreshKey(k => k + 1);
              }}
              onLogout={() => { setBecomeReferrerWizard(false); handleLogout(); }}
              onExit={() => setBecomeReferrerWizard(false)}
            /></>
          ) : (
            <div className="app-shell">
              <div className="app-canvas">
                <CollapsibleSidebar tabs={sidebarTabs} activeTab={activeNavTab} onTabChange={handleNavChange} onLogout={handleLogout} />
                <div className="flex-1 min-w-0 h-full min-h-0 flex flex-col">
                  <ApplicantFlow
                    onLogout={handleLogout}
                    userEmail={profile.email}
                    userId={profile.uid}
                    userProfile={profile}
                    onBecomeReferrer={() => setBecomeReferrerWizard(true)}
                    hasReferrerRole={multiRoleOptions.some(r => r.id === 'referrer')}
                    roleSwitch={roleSwitchFor('applicant')}
                  />
                  <BottomNav tabs={mobileTabs} activeTab={activeNavTab} onTabChange={handleNavChange} />
                </div>
              </div>
            </div>
          ))}
        </Suspense>
      </ErrorBoundary>
      </WorkspaceGate>
      )}
    </>
    </WorkspaceProvider>
  );
}
